import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Modal,
  CircularProgress,
} from "@mui/material";
import { useReportGenerator } from "./useReportGenerator";
import CategorySelector from "./CategorySelector";
import DateRangeSelector from "./DateRangeSelector";
import ExportOptions from "./ExportOptions";
import ReportTable from "./ReportTable";
import { reportsApi } from "./reportsApi";
import { reportCategoryMap } from "./reportConfig";
import Email from "../../images/managedata/email.svg";
import Print from "../../images/managedata/print.svg";
import RouteLogo from "../../images/image-transparent.png";
import EmailReportModal from "./EmailReportModal";
// import { Tabs, Tab } from "@mui/material";
import AnalyticsChart from "./Analytics/AnalyticsChart";

const chartPalette = ["#2563EB", "#22C55E", "#8B5CF6", "#F59E0B", "#20A8E0", "#64748B"];

const compactTextSx = {
  fontFamily: "Inter, Roboto, sans-serif",
  letterSpacing: "0px",
};

const englishText = (value) => value;

const getNiceChartStep = (rawStep) => {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
  const exponent = Math.floor(Math.log10(rawStep));
  const magnitude = 10 ** exponent;
  const normalized = rawStep / magnitude;
  const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return niceNormalized * magnitude;
};

const getChartAxisScale = (maxValue) => {
  const step = getNiceChartStep((Number(maxValue) || 1) / 5);
  const axisMax = Math.max(step, step * Math.ceil((Number(maxValue) || 1) / step));
  const tickCount = Math.floor(axisMax / step);
  return {
    axisMax,
    ticks: Array.from({ length: tickCount + 1 }, (_, index) => {
      const value = axisMax - index * step;
      return Number.isInteger(value) ? `${value}` : value.toFixed(1);
    }),
  };
};

const formatChartTick = (value) => (Number.isInteger(value) ? `${value}` : value.toFixed(1));

const getChartAxisLabels = (chart) => {
  const title = String(chart?.title || "").toLowerCase();
  if (title.includes("overspeed")) return { x: "Vehicles", y: "Incidents" };
  if (title.includes("speed")) return { x: "Vehicles", y: chart?.unit || "Speed" };
  if (title.includes("route")) return { x: "Routes", y: "Vehicles" };
  if (title.includes("stop")) return { x: "Stops", y: "Visits" };
  if (title.includes("vehicle")) return { x: "Vehicles", y: "Trips" };
  if (title.includes("status")) return { x: "Status", y: "Count" };
  return { x: "Items", y: chart?.unit || "Value" };
};

const ReportInfoItem = ({ label, value, t }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        ...compactTextSx,
        color: "#64748B",
        fontSize: "9px",
        fontWeight: 500,
        lineHeight: "13px",
        mb: 0.35,
      }}
    >
      {t(label)}
    </Typography>
    <Typography
      sx={{
        ...compactTextSx,
        color: "#001F52",
        fontSize: "10px",
        fontWeight: 700,
        lineHeight: "14px",
        overflowWrap: "anywhere",
      }}
    >
      {value || "N/A"}
    </Typography>
  </Box>
);

const ReportMetricCard = ({ label, value, unit = "", t }) => {
  const displayLabel = unit ? label.replace(/\s*\([^)]*\)\s*$/, "") : label;

  return (
    <Box
      sx={{
        minHeight: 68,
        backgroundColor: "#F8FAFC",
        px: { xs: 1.5, md: 2 },
        py: 1.4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          ...compactTextSx,
          color: "#4B5563",
          fontSize: "9px",
          fontWeight: 700,
          lineHeight: "13px",
          mb: 0.6,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {t(displayLabel)}
      </Typography>
      <Typography
        sx={{
          fontFamily: "Roboto, Inter, sans-serif",
          color: "#001F52",
          fontSize: { xs: "19px", md: "22px" },
          fontWeight: 800,
          lineHeight: "25px",
          overflowWrap: "anywhere",
        }}
      >
        {value}
        {unit ? (
          <Typography
            component="span"
            sx={{
              ...compactTextSx,
              color: "#1F2937",
              fontSize: "14px",
              fontWeight: 500,
              ml: 0.5,
            }}
          >
            {unit}
          </Typography>
        ) : null}
      </Typography>
    </Box>
  );
};

const EmptyChartState = () => (
  <Box
    sx={{
      height: 118,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#94A3B8",
      fontFamily: "Inter, Roboto, sans-serif",
      fontSize: "11px",
      borderTop: "1px solid #E5E7EB",
      mt: 1,
    }}
  >
    No chart data
  </Box>
);

const ChartPanel = ({ chart, t }) => (
  <Box
    sx={{
      minWidth: 0,
      border: "1px solid #D8DEE8",
      borderRadius: "6px",
      backgroundColor: "#FFFFFF",
      p: 1.4,
    }}
  >
    <Typography
      sx={{
        ...compactTextSx,
        color: "#001F52",
        fontSize: "10px",
        fontWeight: 700,
        lineHeight: "14px",
        mb: 1.5,
      }}
    >
      {t(chart.title)}
    </Typography>
    <ChartRenderer chart={chart} />
  </Box>
);

const VerticalBarChart = ({ chart }) => {
  const itemLimit = Number(chart.maxItems) > 0 ? Number(chart.maxItems) : 4;
  const items = (chart.items || []).slice(0, itemLimit);
  if (!items.length) return <EmptyChartState />;

  const plotHeight = 102;
  const maxValue = Math.max(
    chart.maxValue || 0,
    chart.threshold || 0,
    ...items.map((item) => Number(item.value) || 0),
    1
  );
  const { axisMax, ticks } = getChartAxisScale(maxValue);
  const axisLabels = getChartAxisLabels(chart);
  const thresholdTop =
    chart.threshold && axisMax
      ? `${Math.max(0, Math.min(100, 100 - (chart.threshold / axisMax) * 100))}%`
      : null;

  return (
    <Box sx={{ position: "relative", height: 164, pt: 0.5 }}>
      <Typography
        sx={{
          ...compactTextSx,
          position: "absolute",
          left: -8,
          top: 60,
          transform: "rotate(-90deg)",
          color: "#64748B",
          fontSize: "8px",
          fontWeight: 600,
          width: 58,
          textAlign: "center",
        }}
      >
        {axisLabels.y}
      </Typography>
      <Box
        sx={{
          position: "absolute",
          left: 22,
          top: 5,
          bottom: 48,
          width: 22,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-end",
          pr: 0.7,
        }}
      >
        {ticks.map((tick) => (
          <Typography
            key={tick}
            sx={{
              ...compactTextSx,
              color: "#94A3B8",
              fontSize: "8px",
              fontWeight: 500,
              lineHeight: "10px",
            }}
          >
            {tick}
          </Typography>
        ))}
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: 46,
          right: 0,
          top: 5,
          bottom: 48,
          borderLeft: "1px solid #D8DEE8",
          borderBottom: "1px solid #D8DEE8",
        }}
      >
        {thresholdTop ? (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: thresholdTop,
              borderTop: "1px dashed #EF4444",
              zIndex: 1,
            }}
          >
            <Typography
              sx={{
                ...compactTextSx,
                color: "#EF4444",
                fontSize: "7px",
                fontWeight: 600,
                textAlign: "right",
                pr: 0.5,
                mt: "-8px",
              }}
            >
              Threshold ({chart.threshold}
              {chart.unit || ""})
            </Typography>
          </Box>
        ) : null}
        <Box
          sx={{
          position: "relative",
          zIndex: 2,
          height: "100%",
          display: "grid",
          gridTemplateColumns: `repeat(${items.length}, minmax(38px, 1fr))`,
          gap: 1.6,
          alignItems: "stretch",
          }}
        >
          {items.map((item, index) => {
            const numericValue = Number(item.value) || 0;
            const barHeight = numericValue > 0
              ? Math.max(8, (numericValue / axisMax) * plotHeight)
              : 0;
            return (
              <Box
                key={`${item.label}-${index}`}
                sx={{
                  minWidth: 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      height: barHeight,
                      width: 28,
                      backgroundColor: item.color || chart.color || "#2563EB",
                      borderRadius: "2px 2px 0 0",
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: 46,
          right: 0,
          bottom: 28,
          height: 16,
          display: "grid",
          gridTemplateColumns: `repeat(${items.length}, minmax(38px, 1fr))`,
          gap: 1.6,
        }}
      >
        {items.map((item, index) => (
          <Typography
            key={`${item.label}-${index}-axis-label`}
            sx={{
              ...compactTextSx,
              color: "#64748B",
              fontSize: "7px",
              fontWeight: 500,
              lineHeight: "10px",
              overflow: "hidden",
              textAlign: "center",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.label}
          </Typography>
        ))}
      </Box>
      <Typography
        sx={{
          ...compactTextSx,
          position: "absolute",
          left: 46,
          right: 0,
          bottom: 4,
          color: "#64748B",
          fontSize: "8px",
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        {axisLabels.x}
      </Typography>
    </Box>
  );
};

const HorizontalBarChart = ({ chart }) => {
  const items = (chart.items || []).slice(0, 5);
  if (!items.length) return <EmptyChartState />;

  const maxValue = Math.max(chart.maxValue || 0, ...items.map((item) => Number(item.value) || 0), 1);
  const { axisMax } = getChartAxisScale(maxValue);
  const axisTicks = [0, axisMax / 2, axisMax].map(formatChartTick);

  return (
    <Box sx={{ minHeight: 164, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.4,
          minHeight: 128,
        }}
      >
        {items.map((item, index) => {
          const width = Math.max(4, ((Number(item.value) || 0) / axisMax) * 100);
          return (
            <Box key={`${item.label}-${index}`} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.45,
                }}
              >
                <Typography
                  sx={{
                    ...compactTextSx,
                    color: "#001F52",
                    fontSize: "9px",
                    fontWeight: 700,
                    lineHeight: "12px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.label}
                </Typography>
                <Typography sx={{ ...compactTextSx, color: "#001F52", fontSize: "9px", fontWeight: 700 }}>
                  {item.value}
                </Typography>
              </Box>
              <Box sx={{ height: 6, backgroundColor: "#EEF2F7", borderRadius: "8px", overflow: "hidden" }}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${width}%`,
                    backgroundColor: item.color || chart.color || "#2563EB",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", color: "#94A3B8", mt: 1 }}>
        {axisTicks.map((tick) => (
          <Typography
            key={tick}
            sx={{
              ...compactTextSx,
              color: "#94A3B8",
              fontSize: "8px",
              fontWeight: 500,
            }}
          >
            {tick}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

const getDonutSegments = (items) => {
  const values = (items || [])
    .slice(0, 5)
    .map((item, index) => ({
      ...item,
      value: Number(item.value) || 0,
      color: item.color || chartPalette[index % chartPalette.length],
    }))
    .filter((item) => item.value > 0);
  const total = values.reduce((sum, item) => sum + item.value, 0);
  let cursor = 0;

  return values.map((item) => {
    const start = cursor;
    const end = start + (item.value / total) * 100;
    cursor = end;
    return { ...item, percent: Math.round((item.value / total) * 100), start, end };
  });
};

const DonutChart = ({ chart }) => {
  const segments = getDonutSegments(chart.items);
  if (!segments.length) return <EmptyChartState />;

  const total = segments.reduce((sum, item) => sum + item.value, 0);
  const background = `conic-gradient(${segments
    .map((segment) => `${segment.color} ${segment.start}% ${segment.end}%`)
    .join(", ")})`;

  return (
    <Box
      sx={{
        minHeight: 132,
        display: "grid",
        gridTemplateColumns: "86px minmax(0, 1fr)",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 82,
          height: 82,
          borderRadius: "50%",
          background,
          display: "grid",
          placeItems: "center",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: "#FFFFFF",
            display: "grid",
            placeItems: "center",
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: "#001F52", fontFamily: "Roboto", fontSize: "18px", fontWeight: 800, lineHeight: 1 }}>
            {total}
          </Typography>
          <Typography sx={{ color: "#64748B", fontFamily: "Inter", fontSize: "7px", fontWeight: 600, lineHeight: 1 }}>
            Total
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, minWidth: 0 }}>
        {segments.map((item, index) => (
          <Box
            key={`${item.label}-${index}`}
            sx={{ display: "grid", gridTemplateColumns: "8px minmax(0, 1fr)", gap: 0.8, alignItems: "center" }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: item.color }} />
            <Typography
              sx={{
                ...compactTextSx,
                color: "#001F52",
                fontSize: "9px",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.label} - {item.value} ({item.percent}%)
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ChartRenderer = ({ chart }) => {
  if (chart.type === "vertical") return <VerticalBarChart chart={chart} />;
  if (chart.type === "donut") return <DonutChart chart={chart} />;
  return <HorizontalBarChart chart={chart} />;
};

const ReportGenerator = ({
  reportType,
  vehicles = [],
  routes = [],
  drivers = [],
  stops = [],
  sites = [],
  users = [],
  devices = [],
  t = { t },
}) => {
  const [selectedCategories, setSelectedCategories] = useState({});
  const [dateOptions, setDateOptions] = useState({
    duration: "today",
    customDateRange: { startDate: "", endDate: "" },
  });
  const [openModal, setOpenModal] = useState(false);
  const [exportFormat, setExportFormat] = useState("docx");
  const {
    generateReport,
    loading,
    error,
    reportData,
    clearError,
    clearReportData,
  } = useReportGenerator();
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [allSites, setAllSites] = useState([]);
  const [settings, setSettings] = useState({});
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const setting = JSON.parse(localStorage.getItem("appSettings"));
    setSettings(setting);
  }, []);

  useEffect(() => {
    if (sites.length > 0) {
      setAllSites(sites);
      setSelectedSiteId(""); // Default to no site
    }
    // Debug stops
    const fetchStops = async () => {
      try {
        const stopsData = await reportsApi.getStopsByOrganization();
        console.log("Available Stops:", stopsData);
      } catch (err) {
        console.error("Error fetching stops:", err);
      }
    };
    fetchStops();
  }, [sites]);

  const handleSiteChange = (e) => {
    clearReportData();
    setSelectedSiteId(e.target.value);
  };

  const handleDateOptionsChange = (nextDateOptions) => {
    clearReportData();
    setDateOptions(nextDateOptions);
  };

  const handleCategoryChange = (nextCategories) => {
    clearReportData();
    setSelectedCategories(nextCategories);
  };

  const handleGenerateReport = async () => {
    try {
      await generateReport({
        reportType,
        selectedCategories,
        dateOptions,
        vehicles,
        routes,
        drivers,
        stops,
        users,
        devices,
        siteId: selectedSiteId,
      });
      // setOpenModal(true);
    } catch (err) {
      // Error handled by hook
    }
  };

  const escapePrintHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const getPrintCellValue = (value) => {
    if (value === null || value === undefined || value === "") return "NA";
    if (Array.isArray(value)) {
      return value.length
        ? value.map((item) => item?.scheduleId || item?.name || item).join(", ")
        : "NA";
    }
    if (typeof value === "object") return JSON.stringify(value);
    return value;
  };

  const handlePrint = () => {
    if (!reportData) {
      alert("No content to print");
      return;
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const printDocument = printWindow.document;
    const printHeaderDetails = headerDetails
      .map(
        (detail) => `
          <div class="info-item">
            <div class="info-label">${escapePrintHtml(detail.label)}</div>
            <div class="info-value">${escapePrintHtml(detail.value || "N/A")}</div>
          </div>
        `
      )
      .join("");
    const printFilterDetails = filterDetails
      .map(
        (detail) => `
          <span class="filter-item">
            <span>${escapePrintHtml(detail.label)}:</span>
            <strong>${escapePrintHtml(detail.value || "N/A")}</strong>
          </span>
        `
      )
      .join("");
    const printMetrics = previewMetrics
      .map(
        (metric) => `
          <div class="metric-card">
            <div class="metric-label">${escapePrintHtml(metric.label)}</div>
            <div class="metric-value">${escapePrintHtml(metric.value)}${
              metric.unit ? `<span>${escapePrintHtml(metric.unit)}</span>` : ""
            }</div>
          </div>
        `
      )
      .join("");
    const emptyChart = `<div class="empty-chart">No chart data</div>`;
    const printCharts = previewCharts
      .map((chart) => {
        const items = chart.items || [];
        const maxValue = Math.max(
          chart.maxValue || 0,
          chart.threshold || 0,
          ...items.map((item) => Number(item.value) || 0),
          1
        );
        let chartBody = emptyChart;

        if (items.length && chart.type === "vertical") {
          const { axisMax, ticks } = getChartAxisScale(maxValue);
          const axisLabels = getChartAxisLabels(chart);
          const verticalItemLimit = Number(chart.maxItems) > 0 ? Number(chart.maxItems) : 4;
          const verticalItems = items.slice(0, verticalItemLimit);
          const verticalColumns = `repeat(${Math.max(verticalItems.length, 1)}, minmax(0, 1fr))`;
          const thresholdTop =
            chart.threshold && axisMax
              ? Math.max(0, Math.min(100, 100 - (chart.threshold / axisMax) * 100))
              : null;
          chartBody = `
            <div class="axis-chart vertical-axis-chart">
              <div class="y-axis-label">${escapePrintHtml(axisLabels.y)}</div>
              <div class="y-axis-ticks">
                ${ticks.map((tick) => `<span>${escapePrintHtml(tick)}</span>`).join("")}
              </div>
              <div class="vertical-chart" style="grid-template-columns:${verticalColumns};">
                ${
                  thresholdTop !== null
                    ? `<div class="threshold-line" style="top:${thresholdTop}%;">
                        <span>Threshold (${escapePrintHtml(chart.threshold)}${escapePrintHtml(chart.unit || "")})</span>
                      </div>`
                    : ""
                }
                ${verticalItems
                  .map((item) => {
                    const percent = Math.max(4, ((Number(item.value) || 0) / axisMax) * 100);
                    return `
                      <div class="vertical-item">
                        <div class="vertical-bar" style="height:${percent}%;background:${escapePrintHtml(
                          item.color || chart.color || "#2563EB"
                        )};"></div>
                      </div>
                    `;
                  })
                  .join("")}
              </div>
              <div class="vertical-label-row" style="grid-template-columns:${verticalColumns};">
                ${verticalItems
                  .map((item) => `<div class="vertical-label">${escapePrintHtml(item.label)}</div>`)
                  .join("")}
              </div>
              <div class="x-axis-label">${escapePrintHtml(axisLabels.x)}</div>
            </div>
          `;
        } else if (items.length && chart.type === "donut") {
          const segments = getDonutSegments(items);
          const total = segments.reduce((sum, item) => sum + item.value, 0);
          const background = `conic-gradient(${segments
            .map((segment) => `${segment.color} ${segment.start}% ${segment.end}%`)
            .join(", ")})`;
          chartBody = `
            <div class="donut-chart">
              <div class="donut" style="background:${escapePrintHtml(background)};">
                <div><strong>${escapePrintHtml(total)}</strong><span>Total</span></div>
              </div>
              <div class="donut-legend">
                ${segments
                  .map(
                    (item) => `
                      <div class="legend-row">
                        <span style="background:${escapePrintHtml(item.color)};"></span>
                        <p>${escapePrintHtml(item.label)} - ${escapePrintHtml(item.value)} (${escapePrintHtml(
                      item.percent
                    )}%)</p>
                      </div>
                    `
                  )
                  .join("")}
                </div>
            </div>
          `;
        } else if (items.length) {
          const { axisMax } = getChartAxisScale(maxValue);
          const axisTicks = [0, axisMax / 2, axisMax].map(formatChartTick);
          chartBody = `
            <div class="horizontal-chart">
              ${items
                .slice(0, 5)
                .map((item) => {
                  const percent = Math.max(4, ((Number(item.value) || 0) / axisMax) * 100);
                  return `
                    <div class="horizontal-item">
                      <div class="horizontal-row-head">
                        <span>${escapePrintHtml(item.label)}</span>
                        <strong>${escapePrintHtml(item.value)}</strong>
                      </div>
                      <div><i style="width:${percent}%;background:${escapePrintHtml(
                    item.color || chart.color || "#2563EB"
                  )};"></i></div>
                    </div>
                  `;
                })
                .join("")}
              <div class="x-axis-ticks">
                ${axisTicks.map((tick) => `<span>${escapePrintHtml(tick)}</span>`).join("")}
              </div>
            </div>
          `;
        }

        return `
          <div class="chart-card">
            <div class="chart-title">${escapePrintHtml(chart.title)}</div>
            ${chartBody}
          </div>
        `;
      })
      .join("");
    const printTables = getReportSections()
      .map((section) => {
        const columns = section.columns || [];
        const rows = section.data || [];
        const tableHead = columns
          .map((column) => `<th>${escapePrintHtml(column.label)}</th>`)
          .join("");
        const tableRows = rows.length
          ? rows
              .map(
                (row) => `
                  <tr>
                    ${columns
                      .map(
                        (column) =>
                          `<td>${escapePrintHtml(getPrintCellValue(row[column.key]))}</td>`
                      )
                      .join("")}
                  </tr>
                `
              )
              .join("")
          : `<tr><td colspan="${columns.length || 1}" class="empty-cell">No Data Available</td></tr>`;

        return `
          <section class="table-section">
            <h2>${escapePrintHtml(section.sectionTitle || getReportName(reportType))}</h2>
            <table class="report-table">
              <thead><tr>${tableHead}</tr></thead>
              <tbody>${tableRows}</tbody>
            </table>
          </section>
        `;
      })
      .join("");

    const printContent = `
      <html>
        <head>
          <title>${escapePrintHtml(getReportName(reportType))} Report</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              background: #FFFFFF;
              color: #000000;
              font-family: Roboto, Arial, sans-serif;
            }
            .report-preview {
              width: 100%;
              padding: 16px;
            }
            .report-page {
              padding: 22px;
              background: #FFFFFF;
            }
            .print-header {
              display: grid;
              grid-template-columns: 1fr 120px;
              align-items: start;
              gap: 16px;
              margin-bottom: 22px;
            }
            .print-header img {
              width: 112px;
              height: auto;
              justify-self: end;
            }
            .print-header h1 {
              margin: 0;
              color: #001F52;
              font-size: 20px;
              line-height: 24px;
              font-weight: 800;
            }
            .info-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 20px;
              padding-bottom: 18px;
              border-bottom: 1px solid #D8DEE8;
            }
            .info-label, .filter-item span, .metric-label {
              color: #64748B;
              font-size: 9px;
              line-height: 13px;
              font-weight: 500;
            }
            .info-value {
              color: #001F52;
              font-size: 10px;
              line-height: 14px;
              font-weight: 700;
            }
            .filter-row {
              display: flex;
              flex-wrap: wrap;
              gap: 18px;
              padding: 12px 0;
              border-bottom: 1px solid #D8DEE8;
            }
            .filter-item strong {
              color: #001F52;
              font-size: 10px;
              line-height: 14px;
              font-weight: 800;
              margin-left: 4px;
            }
            .metric-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
            }
            .metric-card {
              min-height: 58px;
              border: 1px solid #D8DEE8;
              background: #F8FAFC;
              padding: 10px 14px;
            }
            .metric-value {
              color: #001F52;
              font-size: 20px;
              line-height: 23px;
              font-weight: 800;
            }
            .metric-value span {
              font-size: 10px;
              line-height: 14px;
              margin-left: 4px;
            }
            .performance-title, .report-table-title {
              color: #001F52;
              font-size: 13px;
              line-height: 18px;
              font-weight: 800;
              margin: 22px 0 14px;
            }
            .performance-title {
              padding-bottom: 10px;
              border-bottom: 1px solid #D8DEE8;
            }
            .chart-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 28px;
              margin: 0 0 18px;
            }
            .chart-card {
              break-inside: avoid;
              border: 1px solid #D8DEE8;
              border-radius: 6px;
              padding: 10px;
            }
            .chart-title {
              color: #001F52;
              font-size: 10px;
              line-height: 14px;
              font-weight: 700;
              margin-bottom: 12px;
            }
            .vertical-chart {
              position: relative;
              height: 132px;
              border-left: 1px solid #D8DEE8;
              border-bottom: 1px solid #D8DEE8;
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 12px;
              align-items: end;
            }
            .vertical-item {
              min-width: 0;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: flex-end;
              flex-direction: column;
              text-align: center;
            }
            .vertical-bar {
              width: 28px;
              min-height: 8px;
              max-height: 102px;
              border-radius: 2px 2px 0 0;
            }
            .vertical-label {
              color: #64748B;
              font-size: 7px;
              line-height: 10px;
              margin-top: 6px;
              width: 100%;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .threshold-line {
              position: absolute;
              left: 0;
              right: 0;
              border-top: 1px dashed #EF4444;
              z-index: 2;
            }
            .axis-chart {
              min-height: 164px;
            }
            .vertical-axis-chart {
              display: grid;
              grid-template-columns: 16px 24px 1fr;
              grid-template-rows: 132px 16px 16px;
              gap: 0 6px;
            }
            .y-axis-label {
              color: #64748B;
              font-size: 8px;
              font-weight: 600;
              writing-mode: vertical-rl;
              transform: rotate(180deg);
              text-align: center;
              align-self: center;
            }
            .y-axis-ticks {
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              align-items: flex-end;
              color: #94A3B8;
              font-size: 8px;
              line-height: 10px;
              padding-right: 4px;
            }
            .x-axis-label {
              color: #64748B;
              font-size: 8px;
              font-weight: 600;
              text-align: center;
              margin-top: 3px;
            }
            .vertical-axis-chart > .x-axis-label {
              grid-column: 3;
              grid-row: 3;
            }
            .vertical-label-row {
              grid-column: 3;
              grid-row: 2;
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 12px;
            }
            .threshold-line span {
              display: block;
              text-align: right;
              color: #EF4444;
              font-size: 7px;
              line-height: 8px;
              margin-top: -8px;
            }
            .horizontal-chart {
              display: flex;
              flex-direction: column;
              gap: 11px;
              min-height: 164px;
            }
            .horizontal-item {
              min-width: 0;
            }
            .horizontal-row-head {
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 8px;
              margin-bottom: 4px;
            }
            .horizontal-item span, .horizontal-item strong, .legend-row p {
              color: #001F52;
              font-size: 9px;
              line-height: 12px;
              margin: 0;
            }
            .horizontal-item span, .legend-row p {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .horizontal-item div {
              height: 6px;
              background: #E5E7EB;
              border-radius: 8px;
              overflow: hidden;
            }
            .horizontal-item i {
              display: block;
              height: 100%;
              border-radius: 8px;
            }
            .x-axis-ticks {
              display: flex;
              justify-content: space-between;
              color: #94A3B8;
              font-size: 8px;
              line-height: 10px;
              margin-top: auto;
            }
            .donut-chart {
              min-height: 132px;
              display: grid;
              grid-template-columns: 86px 1fr;
              gap: 16px;
              align-items: center;
            }
            .donut {
              width: 82px;
              height: 82px;
              border-radius: 50%;
              display: grid;
              place-items: center;
            }
            .donut > div {
              width: 48px;
              height: 48px;
              border-radius: 50%;
              background: #FFFFFF;
              display: grid;
              place-items: center;
              text-align: center;
            }
            .donut strong {
              color: #001F52;
              font-size: 18px;
              line-height: 18px;
            }
            .donut span {
              color: #64748B;
              font-size: 7px;
              line-height: 8px;
              display: block;
            }
            .donut-legend {
              display: grid;
              gap: 6px;
            }
            .legend-row {
              display: grid;
              grid-template-columns: 8px 1fr;
              gap: 6px;
              align-items: center;
            }
            .legend-row span {
              width: 7px;
              height: 7px;
              border-radius: 50%;
            }
            .empty-chart {
              min-height: 132px;
              display: grid;
              place-items: center;
              color: #94A3B8;
              font-size: 11px;
              border-top: 1px solid #E5E7EB;
            }
            .table-section {
              margin-top: 16px;
              break-inside: avoid;
            }
            .table-section h2 {
              color: #183059;
              font-size: 14px;
              line-height: 20px;
              font-weight: 700;
              margin: 0 0 8px;
            }
            .report-table {
              width: 100%;
              border-collapse: collapse;
            }
            .report-table th {
              background: #B7BFCC;
              color: #183059;
              font-size: 11px;
              line-height: 15px;
              font-weight: 700;
              padding: 8px;
              border: 1px solid #D8DEE8;
              text-align: left;
            }
            .report-table td {
              color: #414958;
              font-size: 11px;
              line-height: 15px;
              padding: 8px;
              border: 1px solid #EEEEEE;
            }
            .report-table tbody tr:nth-child(even) td {
              background: #F9F9F9;
            }
            .empty-cell {
              text-align: center;
              color: #647490;
            }
            @media print {
              @page { margin: 12mm; }
              .report-preview { padding: 0; }
              .chart-grid { grid-template-columns: repeat(3, 1fr); }
            }
          </style>
        </head>
        <body>
          <div class="report-preview">
            <div class="report-page">
              <div class="print-header">
                <h1>${escapePrintHtml(getReportName(reportType))} Report</h1>
                <img src="${RouteLogo}" alt="RouteEye" />
              </div>
              <div class="info-grid">${printHeaderDetails}</div>
              <div class="filter-row">${printFilterDetails}</div>
              ${printMetrics ? `<div class="metric-grid">${printMetrics}</div>` : ""}
              ${
                printCharts
                  ? `<div class="performance-title">Performance Breakdown</div><div class="chart-grid">${printCharts}</div>`
                  : ""
              }
              <div class="report-table-title">Report Table</div>
              ${printTables}
            </div>
          </div>
        </body>
      </html>
    `;

    printDocument.write(printContent);
    printDocument.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const getDateRange = (duration) => {
    const today = new Date();
    const formatDate = (date) =>
      `${date.getDate()} ${date.toLocaleString("en-US", {
        month: "long",
      })} ${date.getFullYear()}`;
    let startDate, endDate;

    switch (duration) {
      case "today":
        startDate = endDate = today;
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        endDate = startDate;
        break;
      case "last 7 days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        endDate = today;
        break;
      case "last 30 days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 29);
        endDate = today;
        break;
      case "last 6 months":
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 6);
        endDate = today;
        break;
      case "last 1 year":
        startDate = new Date(today);
        startDate.setFullYear(today.getFullYear() - 1);
        endDate = today;
        break;
      case "custom":
        return dateOptions.customDateRange.startDate &&
          dateOptions.customDateRange.endDate
          ? `${formatDate(
              new Date(dateOptions.customDateRange.startDate),
            )} - ${formatDate(new Date(dateOptions.customDateRange.endDate))}`
          : "None";
      default:
        return "None";
    }
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Enhanced function to add sortable and format properties to dynamic columns
  const enhanceColumns = (backendColumns) => {
    if (!backendColumns || !Array.isArray(backendColumns)) {
      return [];
    }

    return backendColumns.map((col) => {
      const enhanced = {
        key: col.key,
        label: col.label,
        sortable: true, // Default to sortable
      };

      // Add format based on column key patterns
      // if (col.key.toLowerCase().includes("speed")) {
      //   enhanced.format = "speed";
      // } else if (
      //   col.key.toLowerCase().includes("date") ||
      //   col.key.toLowerCase().includes("time")
      // ) {
      //   enhanced.format = "datetime";
      // } else if (col.key.toLowerCase().includes("duration")) {
      //   enhanced.format = "duration";
      //   enhanced.sortable = false;
      // } else if (col.key.toLowerCase().includes("location")) {
      //   enhanced.sortable = false;
      // }

      return enhanced;
    });
  };

  // Transform reportData for ReportTable - now using dynamic columns from backend
  const transformedReportData = reportData
    ? reportData.sections
      ? {
          ...reportData,
          sections: reportData.sections.map((section) => ({
            ...section,
            columns: section.columns ? enhanceColumns(section.columns) : [],
            data: section.data ? section.data : [],
          })),
          columns: reportData.columns ? enhanceColumns(reportData.columns) : [],
          data: reportData.data ? reportData.data : [],
        }
      : {
        ...reportData,
        // Use backend columns if available, otherwise fall back to default columns
        columns: reportData.columns
          ? enhanceColumns(reportData.columns)
          : [
              { key: "deviceId", label: "Device ID", sortable: true },
              { key: "vehicleNumber", label: "Vehicle Number", sortable: true },
              { key: "driverName", label: "Driver", sortable: true },
              { key: "routeName", label: "Route", sortable: true },
              { key: "siteName", label: "Site", sortable: true },
              {
                key: "organizationName",
                label: "Organization",
                sortable: true,
              },
              { key: "status", label: "Status", sortable: true },
              { key: "schedules", label: "Schedules", sortable: true },
              {
                key: "averageSpeed",
                label: "Avg Speed (km/h)",
                sortable: true,
                format: "speed",
              },
              { key: "outOfRouteCount", label: "Out of Route", sortable: true },
            ],
        data: reportData.data ? reportData.data : [],
      }
    : null;

  const getReportName = (type) => {
    if (type === "deviation") return "Route Deviation";
    if (type === "runtime") return "Daily Runtime";
    if (type === "entryExit") {
      return "Stop Entry/Exit";
    }
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getTranslatedReportName = (type) => t(getReportName(type));

  const getReportDescription = (type) => {
    const descriptions = {
      activity:
        "Summary of overall fleet activity including trips, routes, vehicles, drivers, stops, and operational events.",
      overspeed:
        "Detailed report of overspeed incidents with vehicle, driver, location, speed, and timestamp.",
      inventory:
        "Status of all installed devices, sensors, assets, warranties, and maintenance information.",
      runtime:
        "Daily runtime, engine hours, idle time, and operational hours for each vehicle.",
      trip: "Complete trip details including departure, arrival, duration, distance, route followed, and trip status.",
      deviation:
        "Records of vehicles deviating from assigned routes with deviation distance, location, and time.",
      sos: "Emergency SOS alerts with vehicle, driver, location, timestamp, and response status.",
      halt: "Vehicle halt events including halt location, duration, and reason for stoppage.",
      entryExit:
        "Vehicle arrival and departure records for every stop with timestamps and dwell time.",
    };

    return descriptions[type] || "Generate report data using the selected filters.";
  };

  // Add this helper function before the return statement
  const isAnyCategorySelected = Object.values(selectedCategories).some(
    (cat) =>
      cat.isAllSelected || (cat.selectedItems && cat.selectedItems.length > 0),
  );

  const handleEmailClick = () => {
    setEmailModalOpen(true);
  };

  const clearReportParamsFromStorage = () => {
    try {
      localStorage.removeItem("reportCategoryParams");
      console.log("Cleared report params from localStorage");
    } catch (error) {
      console.error("Error clearing report params from localStorage:", error);
    }
  };

  const getFilteredByValue = () => {
    return reportData && reportData.reportType
      ? Array.isArray(reportData.reportType)
        ? reportData.reportType
            .map((type) => type.charAt(0).toUpperCase() + type.slice(1))
            .join(", ")
        : reportData.reportType.charAt(0).toUpperCase() +
          reportData.reportType.slice(1)
      : "None";
  };

  const getUserState = () => {
    try {
      return JSON.parse(localStorage.getItem("userState") || "{}");
    } catch (error) {
      return {};
    }
  };

  const formatShortDate = (date) => {
    if (!date || Number.isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getPreviewDateRange = () => {
    const today = new Date();
    let startDate = new Date(today);
    let endDate = new Date(today);

    switch (dateOptions.duration) {
      case "yesterday":
        startDate.setDate(today.getDate() - 1);
        endDate = new Date(startDate);
        break;
      case "last 7 days":
        startDate.setDate(today.getDate() - 6);
        break;
      case "last 30 days":
        startDate.setDate(today.getDate() - 29);
        break;
      case "last 6 months":
        startDate.setMonth(today.getMonth() - 6);
        break;
      case "last 1 year":
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      case "custom":
        startDate = dateOptions.customDateRange?.startDate
          ? new Date(`${dateOptions.customDateRange.startDate}T00:00:00`)
          : null;
        endDate = dateOptions.customDateRange?.endDate
          ? new Date(`${dateOptions.customDateRange.endDate}T00:00:00`)
          : null;
        break;
      default:
        break;
    }

    const formattedStart = formatShortDate(startDate);
    const formattedEnd = formatShortDate(endDate);

    if (!formattedStart && !formattedEnd) return "None";
    if (!formattedEnd || formattedStart === formattedEnd) return formattedStart;
    return `${formattedStart} to ${formattedEnd}`;
  };

  const getSelectedSiteName = () => {
    const selectedSite =
      allSites.find((site) => String(site.id) === String(selectedSiteId)) ||
      allSites[0];
    return selectedSite?.name || selectedSite?.site_name || "All Sites";
  };

  const getSelectedCategoryName = () => {
    const selectedKeys = Object.keys(selectedCategories || {});
    const availableKeys = (reportCategoryMap[reportType] || []).map(
      (category) => category.key,
    );
    const allCategoriesSelected =
      availableKeys.length > 0 &&
      selectedKeys.length === availableKeys.length &&
      availableKeys.every((key) => selectedKeys.includes(key));

    if (allCategoriesSelected) return "All";
    return selectedKeys.length > 0 ? selectedKeys.join(", ") : getFilteredByValue();
  };

  const getGeneratedBy = () => {
    const user = getUserState()?.user || {};
    const fullName = [user.first_name || user.firstName, user.last_name || user.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return fullName || user.name || user.userName || user.email || "N/A";
  };

  const getOrganizationName = () =>
    getUserState()?.organization?.name || "N/A";

  const getGeneratedOn = () =>
    new Date().toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

  const getReportSections = () => {
    if (transformedReportData?.sections?.length) {
      return transformedReportData.sections;
    }
    return transformedReportData ? [transformedReportData] : [];
  };

  const getAnalyticsSections = () =>
    transformedReportData?.analyticsSections?.length
      ? transformedReportData.analyticsSections
      : getReportSections();

  const toNumber = (value) => {
    const numericValue = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const normalizeKey = (value) =>
    String(value || "")
      .replace(/\s+/g, "")
      .toLowerCase();

  const findColumnByTerms = (columns, terms) =>
    columns.find((column) => {
      const key = normalizeKey(column.key);
      const label = normalizeKey(column.label);
      return terms.some((term) => key.includes(term) || label.includes(term));
    });

  const getLabelColumn = (columns) =>
    findColumnByTerms(columns, [
      "route",
      "vehicle",
      "driver",
      "stop",
      "user",
      "name",
    ]) || columns[0];

  const getMetricColumn = (columns, preferredTerms, fallbackIndex = 0) => {
    const numericColumns = columns.filter((column) =>
      !["number", "id", "name"].some((term) =>
        normalizeKey(column.label || column.key).includes(term)
      )
    );
    return (
      findColumnByTerms(numericColumns, preferredTerms) ||
      numericColumns[fallbackIndex] ||
      columns[1] ||
      columns[0]
    );
  };

  const getSectionCategory = (section) =>
    String(section?.categoryKey || section?.sectionTitle || "").toLowerCase();

  const getLabelColumnForCategory = (columns, category) => {
    const categoryLabelTerms = {
      vehicles: ["vehicle", "vehiclename", "vehiclenumber", "device", "name"],
      routes: ["route", "routename", "name"],
      stops: ["stop", "stopname", "name"],
      drivers: ["driver", "drivername", "name"],
      users: ["user", "username", "email", "name"],
    };

    return (
      findColumnByTerms(columns, categoryLabelTerms[category] || ["name"]) ||
      getLabelColumn(columns)
    );
  };

  const findUsefulMetricColumn = (section, preferredTerms, fallbackIndex = 0) => {
    const rows = section?.data || [];
    const columns = section?.columns || [];
    const preferredColumn = getMetricColumn(columns, preferredTerms, fallbackIndex);
    const hasNumericValue = (column) =>
      column &&
      rows.some((row) => {
        const value = row[column.key];
        return value !== null && value !== undefined && String(value).match(/\d/);
      });

    if (hasNumericValue(preferredColumn)) return preferredColumn;

    return columns.find((column) => {
      const columnKey = normalizeKey(column.label || column.key);
      const isIdentityColumn = ["id", "number", "name", "email"].some((term) =>
        columnKey.includes(term)
      );
      return !isIdentityColumn && hasNumericValue(column);
    });
  };

  const buildMetricChart = ({
    section,
    title,
    labelTerms,
    metricTerms,
    unit = "",
    color = "#2563EB",
    thresholdColor,
    maxValue,
    asPercent = false,
  }) => {
    const rows = section?.data || [];
    const columns = section?.columns || [];
    if (!rows.length || !columns.length) return null;

    const category = getSectionCategory(section);
    const labelColumn =
      findColumnByTerms(columns, labelTerms) ||
      getLabelColumnForCategory(columns, category);
    const metricColumn = findUsefulMetricColumn(section, metricTerms);
    if (!labelColumn || !metricColumn) return null;

    const rawItems = rows
      .map((row, index) => ({
        label: String(row[labelColumn.key] || `Row ${index + 1}`),
        value: toNumber(row[metricColumn.key]),
      }))
      .filter((item) => item.label && item.value >= 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    if (!rawItems.length) return null;

    const rawMax = Math.max(...rawItems.map((item) => item.value), 1);
    const metricKey = normalizeKey(metricColumn.key);
    const metricLabel = normalizeKey(metricColumn.label);
    const shouldUsePercent =
      asPercent || metricKey.includes("percent") || metricLabel.includes("%");
    const items = shouldUsePercent
      ? rawItems.map((item) => ({
          ...item,
          value:
            metricKey.includes("percent") || metricLabel.includes("%")
              ? Math.min(100, Math.round(item.value))
              : Math.round((item.value / rawMax) * 100),
        }))
      : rawItems;

    return {
      title,
      unit: shouldUsePercent ? "%" : unit,
      color,
      maxValue: shouldUsePercent ? 100 : maxValue || rawMax,
      thresholdColor,
      items,
    };
  };

  const buildGroupedChart = ({
    section,
    title,
    groupTerms,
    color = "#2563EB",
  }) => {
    const rows = section?.data || [];
    const columns = section?.columns || [];
    const groupColumn = findColumnByTerms(columns, groupTerms);
    if (!rows.length || !groupColumn) return null;

    const groupedItems = Array.from(
      rows.reduce((map, row) => {
        const label = String(row[groupColumn.key] || "Unassigned").trim();
        map.set(label, (map.get(label) || 0) + 1);
        return map;
      }, new Map())
    )
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    if (!groupedItems.length) return null;

    return {
      title,
      unit: "",
      color,
      maxValue: Math.max(...groupedItems.map((item) => item.value), 1),
      items: groupedItems,
    };
  };

  const buildRouteStatusSummaryChart = (section) => {
    const rows = section?.data || [];
    if (!rows.length) return null;

    const summary = rows.reduce(
      (acc, row) => {
        const trips = toNumber(row.completedTripsOnRoute);
        const vehicleCount = toNumber(row.assignedVehicleCount);

        if (trips > 0) {
          acc.withTrips += 1;
        } else if (vehicleCount > 0) {
          acc.assignedNoTrips += 1;
        } else {
          acc.noVehicles += 1;
        }

        return acc;
      },
      { withTrips: 0, assignedNoTrips: 0, noVehicles: 0 }
    );

    const items = [
      { label: "Routes With Trips", value: summary.withTrips },
      { label: "Assigned Routes With No Trips", value: summary.assignedNoTrips },
      { label: "Routes With No Vehicles", value: summary.noVehicles },
    ];

    if (!items.length) return null;

    return {
      title: "Route Status Summary",
      unit: "",
      color: "#2563EB",
      maxValue: Math.max(...items.map((item) => item.value), 1),
      items,
    };
  };

  const buildDriverStatusSummaryChart = (section) => {
    const rows = section?.data || [];
    if (!rows.length) return null;

    const hasAssignedVehicle = (value) => {
      const normalizedValue = String(value || "").trim().toLowerCase();
      return normalizedValue && normalizedValue !== "none" && normalizedValue !== "n/a";
    };

    const summary = rows.reduce(
      (acc, row) => {
        const trips = toNumber(row.tripsCompleted);
        const isAssigned = hasAssignedVehicle(row.assignedVehicles);

        if (trips > 0) {
          acc.withTrips += 1;
        } else if (isAssigned) {
          acc.assignedNoTrips += 1;
        } else {
          acc.noVehicle += 1;
        }

        return acc;
      },
      { withTrips: 0, assignedNoTrips: 0, noVehicle: 0 }
    );

    const items = [
      { label: "Drivers With Trips", value: summary.withTrips },
      { label: "Assigned Drivers With No Trips", value: summary.assignedNoTrips },
      { label: "Drivers With No Vehicle", value: summary.noVehicle },
    ];

    return {
      title: "Driver Status Summary",
      unit: "",
      color: "#2563EB",
      maxValue: Math.max(...items.map((item) => item.value), 1),
      items,
    };
  };

  const getVehicleCharts = (section, compact = false) => {
    const tripsChart = buildMetricChart({
      section,
      title: "Trips Completed",
      labelTerms: ["vehicle", "vehiclename", "vehiclenumber", "device", "name"],
      metricTerms: ["completedtrips", "trips", "tripcount", "count", "records"],
    });
    if (compact) return [tripsChart].filter(Boolean);

    return [
      buildGroupedChart({
        section,
        title: "Vehicles by Routes",
        groupTerms: ["routename"],
      }),
      tripsChart,
      buildMetricChart({
        section,
        title: "Average Speed by Vehicle (km/h)",
        labelTerms: ["vehicle", "vehiclename", "vehiclenumber", "device", "name"],
        metricTerms: ["averagespeed", "speed"],
        unit: " km/h",
      }),
    ].filter(Boolean);
  };

  const getRouteCharts = (section, compact = false) => {
    const totalVehiclesChart = buildMetricChart({
      section,
      title: "Total Vehicles by Routes",
      labelTerms: ["route", "routename", "name"],
      metricTerms: ["vehicles", "vehiclecount", "completedtrips", "trips", "count"],
    });
    if (compact) return [totalVehiclesChart].filter(Boolean);

    return [
      totalVehiclesChart,
      buildRouteStatusSummaryChart(section),
      buildMetricChart({
        section,
        title: "Trips by Route",
        labelTerms: ["route", "routename", "name"],
        metricTerms: ["completedtripsonroute", "trips", "completedtrips"],
        color: "#22C55E",
      }),
    ].filter(Boolean);
  };

  const getStopCharts = (section, compact = false) => {
    const topStopsChart = buildMetricChart({
      section,
      title: "Top Stops by Visits",
      labelTerms: ["stop", "stopname", "name"],
      metricTerms: ["vehiclesvisited", "visits", "visited", "vehicles", "count"],
    });
    if (compact) return [topStopsChart].filter(Boolean);

    return [
      topStopsChart,
      buildMetricChart({
        section,
        title: "Stops Performance",
        labelTerms: ["stop", "stopname", "name"],
        metricTerms: ["ontime", "performance", "completed", "visits"],
        color: "#22C55E",
        thresholdColor: "#F59E0B",
        asPercent: true,
      }),
    ].filter(Boolean);
  };

  const getFallbackCategoryCharts = (section, compact = false) => {
    const sectionName = section?.sectionTitle || "Category";
    const category = getSectionCategory(section);
    const primaryTitle = category.includes("driver")
      ? "Trips by Driver"
      : `${sectionName} Records`;
    const primaryChart = buildMetricChart({
      section,
      title: primaryTitle,
      labelTerms: ["driver", "user", "name", "email"],
      metricTerms: ["completedtrips", "trips", "count", "records", "alerts"],
    });
    if (compact) return [primaryChart].filter(Boolean);

    if (category.includes("driver")) {
      return [primaryChart, buildDriverStatusSummaryChart(section)].filter(Boolean);
    }

    return [primaryChart].filter(Boolean);
  };

  const getChartsForSection = (section, compact = false) => {
    const category = getSectionCategory(section);
    if (category.includes("vehicle")) return getVehicleCharts(section, compact);
    if (category.includes("route")) return getRouteCharts(section, compact);
    if (category.includes("stop")) return getStopCharts(section, compact);
    return getFallbackCategoryCharts(section, compact);
  };

  const getTotalRecords = () => {
    const sections = getReportSections();
    if (Number.isFinite(Number(transformedReportData?.count))) {
      return Number(transformedReportData.count);
    }
    if (sections.length > 1) {
      return sections.reduce((total, section) => total + (section.data?.length || 0), 0);
    }
    return transformedReportData?.data?.length || 0;
  };

  const getSectionByTerms = (terms) =>
    getReportSections().find((section) => {
      const category = getSectionCategory(section);
      return terms.some((term) => category.includes(term));
    });

  const getAnalyticsSectionByTerms = (terms) =>
    getAnalyticsSections().find((section) => {
      const category = getSectionCategory(section);
      return terms.some((term) => category.includes(term));
    });

  const getAllAnalyticsRows = () =>
    getAnalyticsSections().flatMap((section) => section?.data || []);

  const getAllReportRows = () =>
    getReportSections().flatMap((section) => section?.data || []);

  const findRowValueByTerms = (row, terms) => {
    const entries = Object.entries(row || {});
    for (const term of terms) {
      const normalizedTerm = normalizeKey(term);
      const match = entries.find(([key, value]) => {
        const normalizedKey = normalizeKey(key);
        return (
          normalizedKey.includes(normalizedTerm) &&
          value !== null &&
          value !== undefined &&
          value !== ""
        );
      });
      if (match) return match[1];
    }
    return undefined;
  };

  const findNumberByTerms = (source, terms) => {
    if (!source) return null;
    const entries = Object.entries(source).filter(
      ([, value]) => value === null || typeof value !== "object"
    );

    for (const term of terms) {
      const normalizedTerm = normalizeKey(term);
      const match = entries.find(([key]) => normalizeKey(key).includes(normalizedTerm));
      if (match) {
        const number = toNumber(match[1]);
        if (Number.isFinite(number)) return number;
      }
    }

    return null;
  };

  const getReportMetricNumber = (terms) => {
    const sections = getReportSections();
    const sources = [
      transformedReportData,
      transformedReportData?.metrics,
      transformedReportData?.summary,
      ...sections,
      ...sections.map((section) => section?.metrics),
      ...sections.map((section) => section?.summary),
    ];

    for (const source of sources) {
      const value = findNumberByTerms(source, terms);
      if (value !== null) return value;
    }

    return null;
  };

  const sumRowsByTerms = (rows, terms) =>
    rows.reduce((total, row) => total + toNumber(findRowValueByTerms(row, terms)), 0);

  const averageRowsByTerms = (rows, terms) => {
    const values = rows
      .map((row) => toNumber(findRowValueByTerms(row, terms)))
      .filter((value) => value > 0);
    if (!values.length) return 0;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  };

  const maxRowsByTerms = (rows, terms) =>
    rows.reduce(
      (maxValue, row) => Math.max(maxValue, toNumber(findRowValueByTerms(row, terms))),
      0
    );

  const getSpeedLimit = () =>
    getReportMetricNumber(["maxspeed"]) || toNumber(settings?.maxSpeed) || 0;

  const getDurationMinutes = (value) => {
    if (!value) return 0;
    if (typeof value === "object") {
      if (Number.isFinite(Number(value.minutes))) return Number(value.minutes);
      if (Number.isFinite(Number(value.milliseconds))) return Number(value.milliseconds) / 60000;
      if (value.string) return getDurationMinutes(value.string);
      return 0;
    }

    const text = String(value).toLowerCase();
    if (text.includes("ongoing") || text.includes("n/a")) return 0;

    const dayMatch = text.match(/(\d+(?:\.\d+)?)\s*d/);
    const hourMatch = text.match(/(\d+(?:\.\d+)?)\s*h/);
    const minuteMatch = text.match(/(\d+(?:\.\d+)?)\s*m/);
    const secondMatch = text.match(/(\d+(?:\.\d+)?)\s*s/);
    const clockMatch = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);

    if (dayMatch || hourMatch || minuteMatch || secondMatch) {
      return (
        (Number(dayMatch?.[1]) || 0) * 1440 +
        (Number(hourMatch?.[1]) || 0) * 60 +
        (Number(minuteMatch?.[1]) || 0) +
        (Number(secondMatch?.[1]) || 0) / 60
      );
    }

    if (clockMatch) {
      return (
        (Number(clockMatch[1]) || 0) * 60 +
        (Number(clockMatch[2]) || 0) +
        (Number(clockMatch[3]) || 0) / 60
      );
    }

    return toNumber(value);
  };

  const formatMetricValue = (value, decimals = 0) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "0";
    if (decimals > 0) return number.toFixed(decimals).replace(/\.00$/, "");
    return `${Math.round(number)}`;
  };

  const getLabelFromRow = (row, terms, fallback = "Unknown") => {
    const value = findRowValueByTerms(row, terms);
    const normalizedValue = String(value || "").trim();
    return normalizedValue && normalizedValue.toLowerCase() !== "n/a"
      ? normalizedValue
      : fallback;
  };

  const buildAggregateItems = ({
    rows,
    labelTerms,
    valueTerms,
    mode = "sum",
    color = "#2563EB",
    limit = 5,
  }) => {
    const aggregate = rows.reduce((map, row, index) => {
      const label = getLabelFromRow(row, labelTerms, `Row ${index + 1}`);
      const current = map.get(label) || 0;
      const nextValue = valueTerms ? toNumber(findRowValueByTerms(row, valueTerms)) : 1;
      map.set(label, mode === "max" ? Math.max(current, nextValue) : current + nextValue);
      return map;
    }, new Map());

    return Array.from(aggregate)
      .map(([label, value]) => ({ label, value: Number(value.toFixed ? value.toFixed(2) : value), color }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, limit);
  };

  const withPalette = (items) =>
    items.map((item, index) => ({
      ...item,
      color: chartPalette[index % chartPalette.length],
    }));

  const getUniqueCountByTerms = (rows, terms) =>
    new Set(
      rows
        .map((row) => getLabelFromRow(row, terms, ""))
        .filter((value) => value && value !== "Unknown")
    ).size;

  const getPrimaryLabelTerms = () => [
    "vehicleName",
    "vehicleNumber",
    "routeName",
    "driverName",
    "driversName",
    "stopName",
    "name",
    "deviceId",
  ];

  const getSectionLabel = (section, fallback = "Records") =>
    String(section?.sectionTitle || section?.categoryKey || fallback).replace(/\s*Report$/i, "");

  const buildSectionCountItems = () =>
    withPalette(
      getReportSections()
        .map((section) => ({
          label: getSectionLabel(section),
          value: section?.data?.length || 0,
        }))
        .filter((item) => item.value > 0)
    );

  const buildStatusItems = (rows, statusTerms = ["status"]) => {
    const summary = rows.reduce((map, row) => {
      const label = getLabelFromRow(row, statusTerms, "Unknown");
      map.set(label, (map.get(label) || 0) + 1);
      return map;
    }, new Map());

    return withPalette(
      Array.from(summary)
        .map(([label, value]) => ({ label, value }))
        .filter((item) => item.value > 0)
        .sort((a, b) => b.value - a.value)
    );
  };

  const buildActivityCharts = () => {
    const vehicleRows = getAnalyticsSectionByTerms(["vehicle"])?.data || [];
    const routeRows = getAnalyticsSectionByTerms(["route"])?.data || [];
    const stopRows = getAnalyticsSectionByTerms(["stop"])?.data || [];

    const vehiclesByRouteItems = routeRows.length
      ? routeRows
          .map((row) => ({
            label: getLabelFromRow(row, ["routeName", "route", "name"], "Route"),
            value: toNumber(findRowValueByTerms(row, ["assignedVehicleCount", "vehicles", "vehicleCount"])),
            color: "#2563EB",
          }))
          .filter((item) => item.value > 0)
          .sort((a, b) => b.value - a.value)
          .slice(0, 4)
      : buildAggregateItems({
          rows: vehicleRows,
          labelTerms: ["routeName", "route"],
          mode: "count",
          color: "#2563EB",
          limit: 4,
        });

    const topStopItems = buildAggregateItems({
      rows: stopRows,
      labelTerms: ["stopName", "stop", "name"],
      valueTerms: ["vehiclesVisited", "visits", "visited", "count"],
      color: "#2563EB",
      limit: 5,
    });

    const tripsByVehicleItems = buildAggregateItems({
      rows: vehicleRows,
      labelTerms: ["vehicleName", "vehicleNumber", "deviceId"],
      valueTerms: ["completedTrips", "tripsCompleted", "tripCount"],
      color: "#2563EB",
      limit: 5,
    }).map((item, index) => ({
      ...item,
      color: chartPalette[index % chartPalette.length],
    }));

    return [
      {
        title: "Vehicles by Routes",
        type: "vertical",
        items: vehiclesByRouteItems,
        color: "#2563EB",
        maxValue: Math.max(...vehiclesByRouteItems.map((item) => item.value), 1),
      },
      {
        title: "Top Stops by Visits",
        type: "horizontal",
        items: topStopItems,
        color: "#2563EB",
        maxValue: Math.max(...topStopItems.map((item) => item.value), 1),
      },
      {
        title: "Trips by Vehicle",
        type: "donut",
        items: tripsByVehicleItems,
        color: "#2563EB",
        maxValue: Math.max(...tripsByVehicleItems.map((item) => item.value), 1),
      },
    ];
  };

  const buildOverspeedCharts = () => {
    const vehicleRows = getSectionByTerms(["vehicle"])?.data || [];
    const routeRows = getSectionByTerms(["route"])?.data || [];
    const allRows = getAllReportRows();
    const vehicleChartRows = vehicleRows.length ? vehicleRows : allRows;
    const routeChartRows = routeRows.length ? routeRows : allRows;
    const speedLimit = getSpeedLimit();
    const incidentItems = buildAggregateItems({
      rows: vehicleChartRows,
      labelTerms: ["vehicleName", "vehicleNumber", "deviceId"],
      mode: "count",
      color: "#EF3B2D",
      limit: 4,
    });
    const maxSpeedItems = buildAggregateItems({
      rows: vehicleChartRows,
      labelTerms: ["vehicleName", "vehicleNumber", "deviceId"],
      valueTerms: ["speed"],
      mode: "max",
      color: "#183059",
      limit: 4,
    });
    const routeItems = buildAggregateItems({
      rows: routeChartRows,
      labelTerms: ["routeName", "route"],
      mode: "count",
      color: "#2563EB",
      limit: 5,
    });

    return [
      {
        title: "Overspeed Incidents",
        type: "vertical",
        items: incidentItems,
        color: "#EF3B2D",
        maxValue: Math.max(...incidentItems.map((item) => item.value), 1),
      },
      {
        title: "Maximum Speed by Vehicle (km/h)",
        type: "vertical",
        items: maxSpeedItems,
        color: "#183059",
        unit: " km/h",
        threshold: speedLimit,
        maxValue: Math.max(speedLimit, ...maxSpeedItems.map((item) => item.value), 1),
      },
      {
        title: "Routes with Overspeed Incidents",
        type: "horizontal",
        items: routeItems,
        color: "#2563EB",
        maxValue: Math.max(...routeItems.map((item) => item.value), 1),
      },
    ];
  };

  const buildInventoryCharts = () => {
    const sections = getReportSections();
    const sectionItems = buildSectionCountItems();
    const primarySection = sections.find((section) => section?.data?.length) || sections[0];
    const category = getSectionCategory(primarySection);
    const rows = primarySection?.data || [];
    const charts = [];
    const getRowsForSection = (terms) => getSectionByTerms(terms)?.data || [];
    const hasTermValue = (sourceRows, terms) =>
      sourceRows.some((row) => findRowValueByTerms(row, terms) !== undefined);
    const pushChart = (chart) => {
      if (chart.items?.length) charts.push(chart);
    };
    const buildDonutChart = (title, items) => ({
      title,
      type: "donut",
      items: withPalette(items),
      maxValue: Math.max(...items.map((item) => item.value), 1),
    });
    const buildHorizontalChart = (title, items) => ({
      title,
      type: "horizontal",
      items,
      color: "#2563EB",
      maxValue: Math.max(...items.map((item) => item.value), 1),
    });
    const getUserChartTitle = (labelTerms) =>
      labelTerms.includes("status")
        ? "Users by Status"
        : labelTerms.includes("role") || labelTerms.includes("userRole")
          ? "Users by Role"
          : "User Records";
    const getDriverChartTitle = (labelTerms, vehicleTerms) =>
      labelTerms === vehicleTerms
        ? "Drivers by Vehicle"
        : labelTerms.includes("status")
          ? "Drivers by Status"
          : "Driver Records";

    if (sectionItems.length > 1) {
      pushChart(buildDonutChart("Inventory Records by Category", sectionItems));
    }

    if (sectionItems.length > 1) {
      const vehicleRows = getRowsForSection(["vehicle"]);
      const routeRows = getRowsForSection(["route"]);
      const stopRows = getRowsForSection(["stop"]);
      const userRows = getRowsForSection(["user"]);
      const driverRows = getRowsForSection(["driver"]);

      const vehicleTypeItems = buildAggregateItems({
        rows: vehicleRows,
        labelTerms: ["vehicleType", "type", "category", "make"],
        mode: "count",
        limit: 5,
      });
      pushChart(buildDonutChart("Vehicles by Type", vehicleTypeItems));

      const stopsByRouteItems = stopRows.length
        ? buildAggregateItems({
            rows: stopRows,
            labelTerms: ["routeName", "route"],
            mode: "count",
            color: "#2563EB",
            limit: 5,
          })
        : buildAggregateItems({
            rows: routeRows,
            labelTerms: ["routeName", "route", "name"],
            valueTerms: ["noOfStops", "stops", "stopCount"],
            color: "#2563EB",
            limit: 5,
          });
      pushChart(buildHorizontalChart("Stops by Route", stopsByRouteItems));

      const userLabelTerms = hasTermValue(userRows, ["role", "userRole", "type"])
        ? ["role", "userRole", "type"]
        : hasTermValue(userRows, ["status"])
          ? ["status"]
          : ["userName", "username", "email", "name"];
      const userRoleItems = buildAggregateItems({
        rows: userRows,
        labelTerms: userLabelTerms,
        mode: "count",
        limit: 5,
      });
      pushChart(buildDonutChart(getUserChartTitle(userLabelTerms), userRoleItems));

      const driverVehicleTerms = ["vehicleName", "vehicleNumber", "assignedVehicle", "vehicle"];
      const driverLabelTerms = hasTermValue(driverRows, driverVehicleTerms)
        ? driverVehicleTerms
        : hasTermValue(driverRows, ["status", "driverStatus"])
          ? ["status", "driverStatus"]
          : ["driverName", "driversName", "name"];
      const driverItems = buildAggregateItems({
        rows: driverRows,
        labelTerms: driverLabelTerms,
        color: "#2563EB",
        mode: "count",
        limit: 5,
      });
      pushChart(
        buildHorizontalChart(
          getDriverChartTitle(driverLabelTerms, driverVehicleTerms),
          driverItems
        )
      );

      return charts.filter((chart) => chart.items?.length).slice(0, 6);
    }

    if (category.includes("device")) {
      const typeItems = withPalette(
        buildAggregateItems({
          rows,
          labelTerms: ["deviceType", "sensorType"],
          mode: "count",
          limit: 5,
        })
      );
      const healthItems = withPalette(
        buildStatusItems(rows, ["workingCondition", "status"]).slice(0, 5)
      );
      const assignmentItems = buildAggregateItems({
        rows,
        labelTerms: ["assignmentType", "assignedTo"],
        mode: "count",
        color: "#2563EB",
        limit: 5,
      });
      pushChart(buildDonutChart("Devices by Type", typeItems));
      pushChart(buildDonutChart("Device Health", healthItems));
      pushChart(buildHorizontalChart("Devices by Assignment", assignmentItems));
    } else if (category.includes("vehicle")) {
      const typeItems = buildAggregateItems({
        rows,
        labelTerms: ["vehicleType", "type", "category", "make"],
        mode: "count",
        limit: 5,
      });
      pushChart(buildDonutChart("Vehicles by Type", typeItems));
    } else if (category.includes("route")) {
      const stopItems = buildAggregateItems({
        rows,
        labelTerms: ["routeName", "route"],
        valueTerms: ["noOfStops", "stops"],
        color: "#2563EB",
        limit: 5,
      });
      pushChart(buildHorizontalChart("Stops by Route", stopItems));
    } else if (category.includes("stop")) {
      const routeItems = buildAggregateItems({
        rows,
        labelTerms: ["routeName", "route"],
        mode: "count",
        color: "#2563EB",
        limit: 5,
      });
      pushChart(buildHorizontalChart("Stops by Route", routeItems));
    } else if (category.includes("user")) {
      const userLabelTerms = hasTermValue(rows, ["role", "userRole", "type"])
        ? ["role", "userRole", "type"]
        : hasTermValue(rows, ["status"])
          ? ["status"]
          : ["userName", "username", "email", "name"];
      const roleItems = buildAggregateItems({
        rows,
        labelTerms: userLabelTerms,
        mode: "count",
        limit: 5,
      });
      pushChart(buildDonutChart(getUserChartTitle(userLabelTerms), roleItems));
    } else if (category.includes("driver")) {
      const driverVehicleTerms = ["vehicleName", "vehicleNumber", "assignedVehicle", "vehicle"];
      const driverLabelTerms = hasTermValue(rows, driverVehicleTerms)
        ? driverVehicleTerms
        : hasTermValue(rows, ["status", "driverStatus"])
          ? ["status", "driverStatus"]
          : ["driverName", "driversName", "name"];
      const driverItems = buildAggregateItems({
        rows,
        labelTerms: driverLabelTerms,
        mode: "count",
        color: "#2563EB",
        limit: 5,
      });
      pushChart(
        buildHorizontalChart(
          getDriverChartTitle(driverLabelTerms, driverVehicleTerms),
          driverItems
        )
      );
    } else if (category.includes("schedule")) {
      const scheduleItems = buildAggregateItems({
        rows,
        labelTerms: ["routeName", "route"],
        mode: "count",
        color: "#2563EB",
        limit: 5,
      });
      pushChart(buildHorizontalChart("Schedules by Route", scheduleItems));
    } else {
      const recordItems = buildAggregateItems({
        rows,
        labelTerms: getPrimaryLabelTerms(),
        mode: "count",
        color: "#2563EB",
        limit: 5,
      });
      pushChart(buildHorizontalChart(`${getSectionLabel(primarySection)} Records`, recordItems));
    }

    return charts.filter((chart) => chart.items?.length).slice(0, 3);
  };

  const buildRuntimeCharts = () => {
    const vehicleRows = getAnalyticsSectionByTerms(["vehicle"])?.data || [];
    const routeRows = getAnalyticsSectionByTerms(["route"])?.data || [];
    const rows = getAllAnalyticsRows();
    const runtimeRows = vehicleRows.length ? vehicleRows : rows;
    const tripRows = routeRows.length ? routeRows : rows;
    const runtimeTerms = rows.some((row) =>
      ["totalMovingHours", "totalRuntimeHours", "runtimeHours"].some(
        (key) => row[key] !== undefined && row[key] !== null
      )
    )
      ? ["totalMovingHours", "totalRuntimeHours", "runtimeHours"]
      : ["noOfTrips", "trips", "totalStopsCovered", "noOfStopsCovered"];
    const runtimeItems = buildAggregateItems({
      rows: runtimeRows,
      labelTerms: ["vehicleName", "vehicleNumber", "deviceId", "name"],
      valueTerms: runtimeTerms,
      color: "#2563EB",
      limit: 5,
    });
    const tripItems = buildAggregateItems({
      rows: tripRows,
      labelTerms: ["routeName", "route", "name"],
      valueTerms: ["noOfTrips", "trips", "completedTrips"],
      limit: 5,
    }).map((item, index) => ({
      ...item,
      color: chartPalette[index % chartPalette.length],
    }));
    const idleItems = buildAggregateItems({
      rows: runtimeRows,
      labelTerms: ["vehicleName", "vehicleNumber", "deviceId", "name"],
      valueTerms: ["totalIdleHours", "idleHours", "idle"],
      color: "#F59E0B",
      limit: 5,
    });

    return [
      {
        title: "Vehicles with Highest Runtime (Hrs)",
        type: "horizontal",
        items: runtimeItems,
        unit: runtimeTerms[0].includes("Hours") ? " hrs" : "",
        maxValue: Math.max(...runtimeItems.map((item) => item.value), 1),
      },
      {
        title: "Routes with Highest Trips",
        type: "donut",
        items: tripItems,
        maxValue: Math.max(...tripItems.map((item) => item.value), 1),
      },
      {
        title: "Idle Hours by Vehicle",
        type: "horizontal",
        items: idleItems,
        color: "#F59E0B",
        unit: " hrs",
        maxValue: Math.max(...idleItems.map((item) => item.value), 1),
      },
    ].filter((chart) => chart.items?.length);
  };

  const buildTripCharts = () => {
    const rows = getAllReportRows();
    const tripStatusConfig = [
      { label: "Completed", color: "#22C55E" },
      { label: "Not Completed", color: "#8B5CF6" },
      { label: "In Progress", color: "#2563EB" },
    ];
    const normalizeTripStatus = (status) => {
      const value = String(status || "").toLowerCase();
      if (value.includes("not") || value.includes("incomplete") || value.includes("cancel")) {
        return "Not Completed";
      }
      if (value.includes("progress") || value.includes("ongoing") || value.includes("running")) {
        return "In Progress";
      }
      if (value.includes("complete") || value.includes("completed")) {
        return "Completed";
      }
      return "In Progress";
    };
    const tripItems = buildAggregateItems({
      rows,
      labelTerms: ["routeName", "vehicleName", "vehicleNumber", "driverName"],
      valueTerms: ["noOfTrips", "tripCount", "completedTrips"],
      color: "#2563EB",
      limit: 5,
    });
    const statusCounts = rows.reduce((map, row) => {
      const status = normalizeTripStatus(findRowValueByTerms(row, ["status"]));
      map.set(status, (map.get(status) || 0) + 1);
      return map;
    }, new Map());
    const statusItems = tripStatusConfig
      .map((status) => ({
        ...status,
        value: statusCounts.get(status.label) || 0,
      }))
      .filter((item) => item.value > 0);
    const durationItems = buildAggregateItems({
      rows: rows.map((row) => ({ ...row, durationMinutes: getDurationMinutes(row.duration) })),
      labelTerms: ["routeName", "route"],
      valueTerms: ["durationMinutes"],
      mode: "max",
      color: "#8B5CF6",
      limit: 5,
    });

    return [
      {
        title: "Trips by Route",
        type: "horizontal",
        items: tripItems.length
          ? tripItems
          : buildAggregateItems({ rows, labelTerms: ["routeName", "vehicleName"], mode: "count", limit: 5 }),
        maxValue: Math.max(...tripItems.map((item) => item.value), 1),
      },
      {
        title: "Trip Status",
        type: "donut",
        items: statusItems,
        maxValue: Math.max(...statusItems.map((item) => item.value), 1),
      },
      {
        title: "Longest Trip Duration by Route",
        type: "horizontal",
        items: durationItems,
        color: "#8B5CF6",
        unit: " mins",
        maxValue: Math.max(...durationItems.map((item) => item.value), 1),
      },
    ].filter((chart) => chart.items?.length);
  };

  const buildDeviationCharts = () => {
    const rows = getAllReportRows();
    const routeItems = buildAggregateItems({
      rows,
      labelTerms: ["routeName", "route"],
      mode: "count",
      color: "#EF3B2D",
      limit: 4,
    });
    const vehicleItems = buildAggregateItems({
      rows,
      labelTerms: ["vehicleName", "vehicleNumber", "deviceId"],
      mode: "count",
      color: "#2563EB",
      limit: 5,
    });
    const distanceItems = buildAggregateItems({
      rows,
      labelTerms: ["routeName", "vehicleName", "vehicleNumber"],
      valueTerms: ["distanceMeters", "distance"],
      mode: "max",
      color: "#F59E0B",
      limit: 5,
    });
    const threshold = getReportMetricNumber(["thresholdMeters"]) || maxRowsByTerms(rows, ["thresholdMeters"]);

    return [
      {
        title: "Deviations by Route",
        type: "vertical",
        items: routeItems,
        color: "#EF3B2D",
        maxValue: Math.max(...routeItems.map((item) => item.value), 1),
      },
      {
        title: "Vehicles with Deviations",
        type: "horizontal",
        items: vehicleItems,
        color: "#2563EB",
        maxValue: Math.max(...vehicleItems.map((item) => item.value), 1),
      },
      {
        title: "Maximum Deviation Distance",
        type: "horizontal",
        items: distanceItems,
        color: "#F59E0B",
        unit: " m",
        threshold,
        maxValue: Math.max(threshold, ...distanceItems.map((item) => item.value), 1),
      },
    ].filter((chart) => chart.items?.length);
  };

  const buildSosCharts = () => {
    const rows = getAllReportRows();
    const vehicleItems = buildAggregateItems({
      rows,
      labelTerms: ["vehicleName", "vehicleNumber", "deviceId"],
      mode: "count",
      color: "#EF3B2D",
      limit: 4,
    });
    const routeItems = buildAggregateItems({
      rows,
      labelTerms: ["routeName", "route"],
      mode: "count",
      color: "#2563EB",
      limit: 5,
    });
    const dateItems = buildAggregateItems({
      rows,
      labelTerms: ["date"],
      mode: "count",
      color: "#8B5CF6",
      limit: 5,
    });

    return [
      {
        title: "SOS Alerts by Vehicle",
        type: "vertical",
        items: vehicleItems,
        color: "#EF3B2D",
        maxValue: Math.max(...vehicleItems.map((item) => item.value), 1),
      },
      {
        title: "SOS Alerts by Route",
        type: "horizontal",
        items: routeItems,
        color: "#2563EB",
        maxValue: Math.max(...routeItems.map((item) => item.value), 1),
      },
      {
        title: "SOS Alerts by Date",
        type: "donut",
        items: withPalette(dateItems),
        maxValue: Math.max(...dateItems.map((item) => item.value), 1),
      },
    ].filter((chart) => chart.items?.length);
  };

  const buildHaltCharts = () => {
    const rows = getAllReportRows();
    const vehicleItems = buildAggregateItems({
      rows,
      labelTerms: ["vehicleName", "vehicleNumber", "deviceId"],
      mode: "count",
      color: "#F59E0B",
      limit: 4,
    });
    const typeItems = withPalette(buildStatusItems(rows, ["type"]));
    const durationItems = buildAggregateItems({
      rows: rows.map((row) => ({ ...row, durationMinutes: getDurationMinutes(row.duration) })),
      labelTerms: ["vehicleName", "vehicleNumber", "routeName", "driversName"],
      valueTerms: ["durationMinutes"],
      color: "#8B5CF6",
      limit: 5,
    });

    return [
      {
        title: "Halt Events by Vehicle",
        type: "vertical",
        items: vehicleItems,
        color: "#F59E0B",
        maxValue: Math.max(...vehicleItems.map((item) => item.value), 1),
      },
      {
        title: "Dead vs Inactive",
        type: "donut",
        items: typeItems,
        maxValue: Math.max(...typeItems.map((item) => item.value), 1),
      },
      {
        title: "Halt Duration by Vehicle",
        type: "horizontal",
        items: durationItems,
        color: "#8B5CF6",
        unit: " mins",
        maxValue: Math.max(...durationItems.map((item) => item.value), 1),
      },
    ].filter((chart) => chart.items?.length);
  };

  const getMergedCountMap = (key) => {
    const merged = {};
    getReportSections().forEach((section) => {
      const source = section?.summary?.[key];
      if (!source || typeof source !== "object" || Array.isArray(source)) return;
      Object.entries(source).forEach(([label, value]) => {
        merged[label] = (merged[label] || 0) + (Number(value) || 0);
      });
    });
    return Object.keys(merged).length ? merged : null;
  };

  const buildEntryExitCharts = () => {
    const rows = reportData?.entryExitShared
      ? getReportSections()[0]?.data || []
      : getAllReportRows();
    const stopItems = buildAggregateItems({
      rows,
      labelTerms: ["stopName", "stop"],
      mode: "count",
      color: "#2563EB",
      limit: 5,
    });
    const vehicleCounts = getMergedCountMap("vehicleEventCounts");
    const vehicleItems = vehicleCounts
      ? Object.entries(vehicleCounts)
          .map(([label, value]) => ({
            label,
            value: Number(value) || 0,
            color: "#22C55E",
          }))
          .filter((item) => item.value > 0)
          .sort((a, b) => b.value - a.value)
      : buildAggregateItems({
          rows,
          labelTerms: ["vehicleName", "vehicleNumber", "deviceId"],
          mode: "count",
          color: "#22C55E",
          limit: 8,
        });
    const statusItems = withPalette([
      {
        label: "Completed",
        value:
          getReportMetricNumber(["completedEvents"]) ??
          rows.filter((row) => !String(row.exitTime || "").toLowerCase().includes("still")).length,
      },
      {
        label: "Ongoing",
        value:
          getReportMetricNumber(["ongoingEvents"]) ??
          rows.filter((row) => String(row.exitTime || "").toLowerCase().includes("still")).length,
      },
    ]).filter((item) => item.value > 0);

    return [
      {
        title: "Stop Visits",
        type: "horizontal",
        items: stopItems,
        color: "#2563EB",
        maxValue: Math.max(...stopItems.map((item) => item.value), 1),
      },
      {
        title: "Vehicle Stop Events",
        type: "vertical",
        items: vehicleItems,
        maxItems: Math.max(vehicleItems.length, 1),
        color: "#22C55E",
        maxValue: Math.max(...vehicleItems.map((item) => item.value), 1),
      },
      {
        title: "Entry/Exit Status",
        type: "donut",
        items: statusItems,
        maxValue: Math.max(...statusItems.map((item) => item.value), 1),
      },
    ].filter((chart) => chart.items?.length);
  };

  const buildPreviewCharts = () => {
    if (reportType === "activity") return buildActivityCharts();
    if (reportType === "overspeed") return buildOverspeedCharts();
    if (reportType === "inventory") return buildInventoryCharts();
    if (reportType === "runtime") return buildRuntimeCharts();
    if (reportType === "trip") return buildTripCharts();
    if (reportType === "deviation") return buildDeviationCharts();
    if (reportType === "sos") return buildSosCharts();
    if (reportType === "halt") return buildHaltCharts();
    if (reportType === "entryExit") return buildEntryExitCharts();
    return [];
  };

  const buildReportMetrics = () => {
    const rows = getAllReportRows();

    if (reportType === "activity") {
      const completedTrips =
        getReportMetricNumber(["completedTrips"]) ??
        sumRowsByTerms(rows, ["completedTrips", "tripsCompleted", "completedTripsOnRoute"]);
      const delayedTrips =
        getReportMetricNumber(["delayedTrips"]) ?? sumRowsByTerms(rows, ["delayedTrips"]);
      const averageSpeed =
        getReportMetricNumber(["averageSpeed"]) ?? averageRowsByTerms(rows, ["averageSpeed"]);
      const routeDeviation =
        getReportMetricNumber(["outOfRouteEvents", "routeDeviation", "outOfRouteCount"]) ??
        sumRowsByTerms(rows, ["outOfRouteCount", "routeDeviation"]);

      return [
        { label: "Completed Trips", value: formatMetricValue(completedTrips) },
        { label: "Delayed Trips", value: formatMetricValue(delayedTrips) },
        { label: "Average Speed (km/h)", value: formatMetricValue(averageSpeed, 2), unit: "km/h" },
        { label: "Route Deviation", value: formatMetricValue(routeDeviation) },
      ];
    }

    if (reportType === "overspeed") {
      const speedLimit = getSpeedLimit();
      const maxSpeedRecorded =
        getReportMetricNumber(["maxSpeedRecorded"]) ?? maxRowsByTerms(rows, ["speed"]);
      const highestOverLimit =
        maxRowsByTerms(rows, ["speedExcess", "overLimit"]) ||
        Math.max(0, maxSpeedRecorded - speedLimit);
      const totalIncidents = getReportMetricNumber(["totalOverspeedEvents"]) ?? getTotalRecords();

      return [
        { label: "Threshold (km/h)", value: formatMetricValue(speedLimit), unit: "km/h" },
        { label: "Total Overspeed Incidents", value: formatMetricValue(totalIncidents) },
        { label: "Highest Over Limit", value: formatMetricValue(highestOverLimit), unit: "km/h" },
        { label: "Max Speed Recorded", value: formatMetricValue(maxSpeedRecorded), unit: "km/h" },
      ];
    }

    if (reportType === "inventory") {
      const sectionItems = buildSectionCountItems();
      const metrics = [
        { label: "Total Inventory Records", value: formatMetricValue(getTotalRecords()) },
      ];

      sectionItems.slice(0, 3).forEach((item) => {
        metrics.push({ label: item.label, value: formatMetricValue(item.value) });
      });

      while (metrics.length < 4) {
        const fallbackRows = getAllReportRows();
        metrics.push(
          metrics.length === 1
            ? { label: "Routes Covered", value: formatMetricValue(getUniqueCountByTerms(fallbackRows, ["routeName", "route"])) }
            : metrics.length === 2
              ? { label: "Vehicles", value: formatMetricValue(getUniqueCountByTerms(fallbackRows, ["vehicleName", "vehicleNumber"])) }
              : { label: "Active Categories", value: formatMetricValue(sectionItems.length || 1) }
        );
      }

      return metrics.slice(0, 4);
    }

    if (reportType === "runtime") {
      const totalRuntime = sumRowsByTerms(rows, ["totalMovingHours", "totalRuntimeHours", "runtimeHours"]);
      const totalIdle = sumRowsByTerms(rows, ["totalIdleHours", "idleHours"]);
      const totalTrips = sumRowsByTerms(rows, ["noOfTrips", "completedTrips", "trips"]);
      const averageSpeed = averageRowsByTerms(rows, ["averageSpeed", "avgSpeed"]);

      return [
        { label: "Total Runtime", value: formatMetricValue(totalRuntime, 1), unit: "hrs" },
        { label: "Total Trips", value: formatMetricValue(totalTrips || getTotalRecords()) },
        { label: "Average Speed", value: formatMetricValue(averageSpeed, 1), unit: "km/h" },
        { label: "Idle Hours", value: formatMetricValue(totalIdle, 1), unit: "hrs" },
      ];
    }

    if (reportType === "trip") {
      const explicitTrips = sumRowsByTerms(rows, ["noOfTrips", "tripCount", "completedTrips"]);
      const totalTrips = explicitTrips || getTotalRecords();
      const delayedTrips = rows.filter((row) =>
        String(row.status || "").toLowerCase().includes("delay")
      ).length;
      const cancelledTrips = rows.filter((row) =>
        String(row.status || "").toLowerCase().includes("cancel")
      ).length;
      const averageDuration = averageRowsByTerms(
        rows.map((row) => ({ durationMinutes: getDurationMinutes(row.duration) })),
        ["durationMinutes"]
      );
      const onTimeTrips = Math.max(0, totalTrips - delayedTrips - cancelledTrips);

      return [
        { label: "Total Trips", value: formatMetricValue(totalTrips) },
        { label: "On-Time Trips", value: formatMetricValue(onTimeTrips) },
        { label: "Delayed Trips", value: formatMetricValue(delayedTrips) },
        { label: "Avg Duration", value: formatMetricValue(averageDuration, 1), unit: "mins" },
      ];
    }

    if (reportType === "deviation") {
      return [
        { label: "Total Deviations", value: formatMetricValue(getTotalRecords()) },
        { label: "Routes Affected", value: formatMetricValue(getUniqueCountByTerms(rows, ["routeName", "route"])) },
        { label: "Vehicles Affected", value: formatMetricValue(getUniqueCountByTerms(rows, ["vehicleName", "vehicleNumber"])) },
        { label: "Max Distance", value: formatMetricValue(maxRowsByTerms(rows, ["distanceMeters", "distance"])), unit: "m" },
      ];
    }

    if (reportType === "sos") {
      return [
        { label: "Total SOS Alerts", value: formatMetricValue(getTotalRecords()) },
        { label: "Vehicles Involved", value: formatMetricValue(getUniqueCountByTerms(rows, ["vehicleName", "vehicleNumber"])) },
        { label: "Routes Affected", value: formatMetricValue(getUniqueCountByTerms(rows, ["routeName", "route"])) },
        { label: "Drivers Involved", value: formatMetricValue(getUniqueCountByTerms(rows, ["driverName", "driversName"])) },
      ];
    }

    if (reportType === "halt") {
      const inactiveEvents =
        getReportMetricNumber(["totalInactivePeriods"]) ??
        rows.filter((row) => String(row.type || "").toLowerCase().includes("inactive")).length;
      const deadEvents =
        getReportMetricNumber(["totalDeadPeriods"]) ??
        rows.filter((row) => String(row.type || "").toLowerCase().includes("dead")).length;
      const averageDuration = averageRowsByTerms(
        rows.map((row) => ({ durationMinutes: getDurationMinutes(row.duration) })),
        ["durationMinutes"]
      );

      return [
        { label: "Total Halt Events", value: formatMetricValue(rows.length || inactiveEvents + deadEvents) },
        { label: "Inactive Events", value: formatMetricValue(inactiveEvents) },
        { label: "Dead Events", value: formatMetricValue(deadEvents) },
        { label: "Avg Duration", value: formatMetricValue(averageDuration, 1), unit: "mins" },
      ];
    }

    if (reportType === "entryExit") {
      const completedEvents =
        getReportMetricNumber(["completedEvents"]) ??
        rows.filter((row) => !String(row.exitTime || "").toLowerCase().includes("still")).length;
      const ongoingEvents =
        getReportMetricNumber(["ongoingEvents"]) ??
        rows.filter((row) => String(row.exitTime || "").toLowerCase().includes("still")).length;

      return [
        { label: "Total Stop Events", value: formatMetricValue(getReportMetricNumber(["totalEvents"]) ?? getTotalRecords()) },
        { label: "Completed Events", value: formatMetricValue(completedEvents) },
        { label: "Ongoing Events", value: formatMetricValue(ongoingEvents) },
        { label: "Unique Stops", value: formatMetricValue(getReportMetricNumber(["uniqueStopsVisited"]) ?? getUniqueCountByTerms(rows, ["stopName", "stop"])) },
      ];
    }

    return [];
  };

  const previewCharts = buildPreviewCharts();
  const previewMetrics = buildReportMetrics();
  const headerDetails = [
    { label: "Organization", value: getOrganizationName() },
    { label: "Site", value: getSelectedSiteName() },
    { label: "Generated By", value: getGeneratedBy() },
    { label: "Generated On", value: getGeneratedOn() },
  ];
  const filterDetails = [
    { label: "Categories", value: getSelectedCategoryName() },
    { label: "Date Range", value: getPreviewDateRange() },
    { label: "Total Records", value: `${getTotalRecords()}` },
  ];
  const previewDetails = [
    {
      label: "Organization",
      value: getOrganizationName(),
    },
    { label: "Site", value: getSelectedSiteName() },
    { label: "Generated By", value: getGeneratedBy() },
    {
      label: "Category Selected",
      value: getSelectedCategoryName(),
    },
    {
      label: "Date Range Selected",
      value: getPreviewDateRange(),
    },
    {
      label: "Total Records",
      value: `${getTotalRecords()}`,
    },
    ...(reportType === "overspeed"
      ? [
          {
            label: "Speed Limit",
            value: `${settings?.maxSpeed || 0} km/h`,
          },
        ]
      : []),
    ...(reportType === "halt"
      ? [
          {
            label: "Inactive Time",
            value:
              transformedReportData?.summary?.totalInactiveTime?.string ||
              "N/A",
          },
          {
            label: "Dead Time",
            value:
              transformedReportData?.summary?.totalDeadTime?.string || "N/A",
          },
        ]
      : []),
  ];
  const exportReportData = transformedReportData
    ? {
        ...transformedReportData,
        previewDetails,
        previewHeaderDetails: headerDetails,
        previewFilterDetails: filterDetails,
        previewMetrics,
        previewCharts,
        previewTitle: `${getReportName(reportType)} Report Preview`,
        generatedOn: getGeneratedOn(),
      }
    : transformedReportData;

  return (
    <>
      <Box sx={{ p: "0px" }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">
            {error}
            <Button onClick={clearError}>×</Button>
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 360px" },
                gap: { xs: 3, lg: 4 },
                alignItems: "start",
                px: { xs: 0, lg: 2 },
              }}
            >
              <Box sx={{ pt: { xs: 0, lg: 1.5 } }}>
                <Typography
                  sx={{
                    color: "#1F2937",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "22px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    letterSpacing: "0px",
                    mb: 1.5,
                  }}
                >
                  {getTranslatedReportName(reportType)} {t("Report")}
                </Typography>
                <Typography
                  sx={{
                    color: "#6B7280",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "18px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    letterSpacing: "0px",
                    maxWidth: 560,
                  }}
                >
                  {t(getReportDescription(reportType))}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  columnGap: 1.5,
                  rowGap: 1.5,
                  "& .report-filter-label": {
                    color: "#64748B",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "10px",
                    fontWeight: 400,
                    lineHeight: "15px",
                    letterSpacing: "0px",
                    mb: 0.5,
                  },
                  "& .report-filter-control .MuiInputBase-root, & .report-filter-control .MuiSelect-select":
                    {
                      backgroundColor: "#FFFFFF",
                      color: "#000000",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "16px",
                      letterSpacing: "0px",
                    },
                  "& .report-filter-control .MuiSelect-select": {
                    py: "6px",
                    px: "6px",
                    minHeight: "unset",
                  },
                  "& .report-filter-control .MuiOutlinedInput-root": {
                    height: "30px",
                    borderRadius: "4px",
                    padding: 0,
                  },
                  "& .report-filter-control fieldset": {
                    borderColor: "#E5E7EB",
                    borderWidth: "1px",
                  },
                  "& .category-selector": {
                    display: "contents",
                  },
                }}
              >
                <Box className="report-filter-control">
                  <Typography className="report-filter-label">
                    {t("Sites")}
                  </Typography>
                  <FormControl sx={{ width: "100%" }}>
                    {allSites.length === 1 ? (
                      <Box
                        sx={{
                          background: "#FFFFFF",
                          borderRadius: "4px",
                          height: "30px",
                          border: "1px solid #E5E7EB",
                          display: "flex",
                          alignItems: "center",
                          p: "6px",
                          color: "#000000",
                          fontFamily: "Inter, sans-serif",
                          fontSize: "12px",
                          fontWeight: 400,
                          lineHeight: "16px",
                        }}
                      >
                        {allSites[0].name || allSites[0].site_name}
                      </Box>
                    ) : (
                      <Select
                        value={
                          selectedSiteId ||
                          (allSites.length > 0 ? allSites[0].id : "")
                        }
                        onChange={handleSiteChange}
                        sx={{
                          background: "#FFFFFF",
                          borderRadius: "4px",
                          height: "30px",
                          fontFamily: "Inter, sans-serif",
                          fontSize: "12px",
                          fontWeight: 400,
                          lineHeight: "16px",
                          "& fieldset": {
                            borderColor: "#E5E7EB",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>{t("All Sites")}</em>
                        </MenuItem>
                        {allSites.map((site) => (
                          <MenuItem key={site.id} value={site.id}>
                            {site.name || site.site_name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                </Box>

                <Box className="report-filter-control">
                  <Typography className="report-filter-label">
                    {t("Duration")}
                  </Typography>
                  <DateRangeSelector
                    dateOptions={dateOptions}
                    onChange={handleDateOptionsChange}
                    sx={{ width: "100%" }}
                    compact
                  />
                </Box>

                <CategorySelector
                  reportType={reportType}
                  onCategoryChange={handleCategoryChange}
                  initialCategories={selectedCategories}
                  vehicles={vehicles}
                  routes={routes}
                  drivers={drivers}
                  stops={stops}
                  users={users}
                  devices={devices}
                  showFieldLabels
                  t={t}
                />

                <Button
                  variant="contained"
                  onClick={handleGenerateReport}
                  disabled={loading || !isAnyCategorySelected}
                  sx={{
                    gridColumn: { xs: "1", sm: "1 / -1" },
                    backgroundColor: "#183059",
                    color: "#F6F6F7",
                    "&:hover": { backgroundColor: "#102647" },
                    "&.Mui-disabled": {
                      backgroundColor: "#9CA3AF",
                      color: "#F6F6F7",
                    },
                    height: "32px",
                    px: 3,
                    py: "8px",
                    gap: "8px",
                    textTransform: "none",
                    borderRadius: "6px",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "12px",
                    fontWeight: 600,
                    lineHeight: "16px",
                    letterSpacing: "0px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    boxShadow: "none",
                  }}
                >
                  {loading ? <CircularProgress size={22} /> : t("Generate Report")}
                </Button>
              </Box>
            </Box>
            {/* <Modal
            open={openModal}
            onClose={() => {
              setOpenModal(false);
            }}
          > */}
            {/* Tabs Section */}
            {reportData && (
              <Box
                sx={{
                  flex: 1,
                  border: "1px solid #95A0B3",
                  borderRadius: "16px",
                  boxShadow: "-2px 2px 8px 0px #0C305A40",
                  ml: { xs: 0, md: 2 },
                  mt: 3,
                  p: { xs: 1.5, md: 3 },
                  backgroundColor: "#FFFFFF",
                }}
              >
                <Box
                  sx={{
                    border: "1px dashed #183059",
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    p: { xs: 2, md: 3 },
                    color: "#001F52",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
                      alignItems: "start",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Typography
                      sx={{
                        ...compactTextSx,
                        color: "#001F52",
                        fontSize: { xs: "18px", md: "20px" },
                        fontWeight: 800,
                        lineHeight: "24px",
                      }}
                    >
                      {getReportName(reportType)} Report
                    </Typography>
                    <Box
                      component="img"
                      src={RouteLogo}
                      alt="RouteEye"
                      sx={{
                        width: { xs: 92, md: 112 },
                        height: "auto",
                        justifySelf: { xs: "start", sm: "end" },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(4, minmax(0, 1fr))",
                      },
                      gap: { xs: 1.5, md: 3 },
                      pb: 2.5,
                      borderBottom: "1px solid #D8DEE8",
                    }}
                  >
                    {headerDetails.map((detail) => (
                      <ReportInfoItem key={detail.label} {...detail} t={englishText} />
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: { xs: 1.5, md: 2.2 },
                      py: 1.6,
                      borderBottom: "1px solid #D8DEE8",
                    }}
                  >
                    {filterDetails.map((detail) => (
                      <Box
                        key={detail.label}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.65,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            ...compactTextSx,
                            color: "#64748B",
                            fontSize: "9px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {detail.label}:
                        </Typography>
                        <Typography
                          sx={{
                            ...compactTextSx,
                            color: "#001F52",
                            fontSize: "10px",
                            fontWeight: 800,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {detail.value || "N/A"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {previewMetrics.length > 0 && (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          md: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 0,
                        mt: 1.8,
                        mb: 2.2,
                        border: "1px solid #D8DEE8",
                        borderRadius: "6px",
                        backgroundColor: "#F8FAFC",
                        overflow: "hidden",
                        boxShadow: "none",
                        "& > *": { borderRadius: 0 },
                        "& > *:not(:last-of-type)": {
                          borderRight: { xs: "none", sm: "1px solid #D8DEE8" },
                          borderBottom: { xs: "1px solid #D8DEE8", sm: "none" },
                        },
                        "& > *:nth-of-type(-n + 2)": {
                          borderBottom: { sm: "1px solid #D8DEE8", md: "none" },
                        },
                        "& > *:nth-of-type(2)": {
                          borderRight: { sm: "none", md: "1px solid #D8DEE8" },
                        },
                      }}
                    >
                      {previewMetrics.map((metric) => (
                        <ReportMetricCard key={metric.label} {...metric} t={englishText} />
                      ))}
                    </Box>
                  )}

                  {previewCharts.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography
                        sx={{
                          ...compactTextSx,
                          color: "#001F52",
                          fontSize: "13px",
                          fontWeight: 800,
                          lineHeight: "18px",
                          pb: 1.4,
                          mb: 2,
                          borderBottom: "1px solid #D8DEE8",
                        }}
                      >
                        Performance Breakdown
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(3, minmax(0, 1fr))",
                          },
                          gap: { xs: 2.4, md: 4 },
                        }}
                      >
                        {previewCharts.map((chart) => (
                          <ChartPanel key={chart.title} chart={chart} t={englishText} />
                        ))}
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Typography
                      sx={{
                        ...compactTextSx,
                        color: "#001F52",
                        fontSize: "13px",
                        fontWeight: 800,
                        lineHeight: "18px",
                        mb: 2,
                      }}
                    >
                      Report Table
                    </Typography>
                    <ReportTable reportData={transformedReportData} />
                  </Box>
                </Box>

                <Box
                  sx={{
                    backgroundColor: "#647490",
                    p: { xs: 0.75, md: 1 },
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: { xs: 2, md: 3 },
                    minHeight: { xs: "auto", sm: "64px" },
                    borderRadius: "8px",
                    gap: { xs: 1.5, sm: 1 },
                  }}
                >
                  <ExportOptions
                    reportData={exportReportData}
                    exportFormat={exportFormat}
                    setExportFormat={setExportFormat}
                    reportType={reportType}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleEmailClick}
                      sx={{
                        width: { xs: "50%", sm: "90px" },
                        height: "40px",
                        borderRadius: "8px",
                        border: "1px solid #183059",
                        backgroundColor: "#20A8E0",
                        color: "white",
                        fontSize: { xs: "11px", md: "14px" },
                        textTransform: "none",
                        boxShadow: "none",
                        "&:hover": { backgroundColor: "#00acc1", boxShadow: "none" },
                      }}
                    >
                      <Box
                        component="img"
                        src={Email}
                        alt="mail"
                        sx={{ mr: 0.6, width: { xs: 14, md: 18 }, height: { xs: 14, md: 18 } }}
                      />
                      Mail
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handlePrint}
                      sx={{
                        width: { xs: "50%", sm: "90px" },
                        height: "40px",
                        borderRadius: "8px",
                        border: "1px solid #183059",
                        backgroundColor: "#20A8E0",
                        color: "white",
                        fontSize: { xs: "11px", md: "14px" },
                        textTransform: "none",
                        boxShadow: "none",
                        "&:hover": { backgroundColor: "#00acc1", boxShadow: "none" },
                      }}
                    >
                      <Box
                        component="img"
                        src={Print}
                        alt="print"
                        sx={{ mr: 0.6, width: { xs: 14, md: 18 }, height: { xs: 14, md: 18 } }}
                      />
                      Print
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
            {false && reportData && (
              <Box
                sx={{
                  flex: 1,
                  p: 1,
                  border: "1px solid #95A0B3",
                  borderRadius: "16px",
                  boxShadow: "-2px 2px 8px 0px #0C305A40",
                  ml: 2,
                  mt: 3,
                  padding: "24px",
                }}
              >
                <Box
                  sx={{
                    mt: 2,
                    bgcolor: "#F2F2F3",
                    border: "1px dashed #183059",
                    borderRadius: "8px",
                    // position: "absolute",
                    // top: "50%",
                    // left: "50%",
                    // transform: "translate(-50%, -50%)",
                    // width: { xs: "95%", sm: "85%", md: "70%" },
                    // maxHeight: { xs: "95vh", sm: "90vh" },
                    // bgcolor: "background.paper",
                    // boxShadow: 24,
                    // borderRadius: 2,
                    // overflow: "hidden",
                    // padding: { xs: "15px", sm: "20px", md: "30px" },
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "#F2F2F3",
                      border: "1px dashed #183059",
                      borderRadius: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#e8eaf6",
                        p: { xs: 1, sm: 1.5, md: 2 },
                        borderBottom: "1px solid #ddd",
                        position: "relative",
                        borderRadius: "8px 8px 0px 0px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "1fr auto 1fr",
                          },
                          alignItems: "start",
                          gap: 1.5,
                          mb: 1.5,
                        }}
                      >
                        <Box
                          component="img"
                          src={RouteLogo}
                          alt="RouteEye"
                          sx={{
                            width: { xs: 96, md: 112 },
                            height: "auto",
                          }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Roboto",
                            fontWeight: 700,
                            fontSize: { xs: "20px", sm: "26px", md: "32px" },
                            lineHeight: 1.1,
                            textAlign: { xs: "left", md: "center" },
                            color: "#183059",
                          }}
                        >
                          {getReportName(reportType)} Report Preview
                        </Typography>
                        <Box
                          sx={{
                            textAlign: { xs: "left", md: "right" },
                            color: "#183059",
                            fontFamily: "Roboto",
                            fontSize: { xs: "10px", md: "12px" },
                            fontWeight: 600,
                            lineHeight: 1.2,
                          }}
                        >
                          <Box>Generated On</Box>
                          <Box>{getGeneratedOn()}</Box>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                            lg: "repeat(3, minmax(0, 1fr))",
                          },
                          gap: { xs: 1.25, md: 1.5 },
                        }}
                      >
                        {previewDetails.map((detail) => (
                          <Box
                            key={detail.label}
                            sx={{
                              backgroundColor: "#FFFFFF",
                              border: "1px solid #D8DEE8",
                              borderRadius: "6px",
                              p: { xs: "8px 10px", md: "10px 14px" },
                              minHeight: 52,
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#647490",
                                fontFamily: "Roboto",
                                fontSize: { xs: "10px", md: "12px" },
                                fontWeight: 600,
                                lineHeight: 1.2,
                                mb: 0.75,
                              }}
                            >
                              {detail.label}
                            </Typography>
                            <Typography
                              sx={{
                                color: "#000000",
                                fontFamily: "Roboto",
                                fontSize: { xs: "12px", md: "14px" },
                                fontWeight: 700,
                                lineHeight: 1.25,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {detail.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {previewCharts.length > 0 && (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              md: "repeat(3, minmax(0, 1fr))",
                            },
                            gap: { xs: 1.5, md: 2 },
                            mt: 2,
                          }}
                        >
                          {previewCharts.map((chart) => (
                            <Box
                              key={chart.title}
                              sx={{
                                backgroundColor: "#FFFFFF",
                                border: "1px solid #E5E7EB",
                                borderRadius: "4px",
                                p: 2,
                                minHeight: 170,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#111827",
                                  fontFamily: "Roboto",
                                  fontWeight: 700,
                                  fontSize: "14px",
                                  mb: 2,
                                }}
                              >
                                {chart.title}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1.25,
                                }}
                              >
                                {chart.items.slice(0, 6).map((item) => {
                                  const percent = Math.min(
                                    100,
                                    chart.maxValue
                                      ? (item.value / chart.maxValue) * 100
                                      : 0
                                  );
                                  const barColor =
                                    chart.thresholdColor && item.value < 80
                                      ? chart.thresholdColor
                                      : chart.color;

                                  return (
                                    <Box key={item.label}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          gap: 1,
                                          mb: 0.5,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            color: "#183059",
                                            fontFamily: "Roboto",
                                            fontWeight: 600,
                                            fontSize: "10px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {item.label}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            color: "#183059",
                                            fontFamily: "Roboto",
                                            fontWeight: 700,
                                            fontSize: "10px",
                                            flexShrink: 0,
                                          }}
                                        >
                                          {item.value}
                                          {chart.unit}
                                        </Typography>
                                      </Box>
                                      <Box
                                        sx={{
                                          height: 6,
                                          backgroundColor: "#EEF2F7",
                                          borderRadius: "999px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: `${percent}%`,
                                            height: "100%",
                                            backgroundColor: barColor,
                                            borderRadius: "999px",
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  color: "#9CA3AF",
                                  fontFamily: "Roboto",
                                  fontSize: "10px",
                                  mt: 1.5,
                                }}
                              >
                                <Box>0{chart.unit}</Box>
                                <Box>
                                  {chart.maxValue}
                                  {chart.unit}
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* <Box
                    sx={{
                      position: "absolute",
                      top: { xs: 4, sm: 6, md: 8 },
                      right: { xs: 4, sm: 6, md: 8 },
                      width: { xs: 20, sm: 22, md: 24 },
                      height: { xs: 20, sm: 22, md: 24 },
                      backgroundColor: "#EE3B2B",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "white",
                      fontSize: { xs: "12px", sm: "13px", md: "14px" },
                      fontWeight: "bold",
                    }}
                    onClick={() => {
                      setOpenModal(false);
                      clearReportParamsFromStorage();
                    }}
                  >
                    ×
                  </Box> */}
                      <Typography
                        variant="h6"
                        sx={{
                          display: "none",
                          fontFamily: "Roboto",
                          fontWeight: 600,
                          fontSize: { xs: "18px", sm: "24px", md: "30px" },
                          mb: 1,
                          textAlign: "center",
                          color: "#183059",
                          pr: { xs: 3, sm: 4, md: 0 },
                        }}
                      >
                        {getReportName(reportType)} Report Preview
                      </Typography>
                      <Box
                        sx={{
                          display: "none",
                          alignItems: "flex-start",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: { xs: "10px", sm: "15px", md: "20px" },
                          fontSize: "12px",
                          justifyContent: {
                            xs: "flex-start",
                            sm: "space-between",
                          },
                          mt: { xs: 1, sm: 1.5, md: 2 },
                        }}
                      >
                        {/* Date Range */}
                        <Box>
                          <Typography
                            sx={{
                              color: "#414958",
                              fontFamily: "Roboto",
                              fontWeight: 600,
                              fontSize: { xs: "12px", sm: "14px", md: "16px" },
                            }}
                          >
                            Duration
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: { xs: "10px", sm: "12px", md: "14px" },
                              color: "#183059",
                            }}
                          >
                            {getDateRange(dateOptions.duration)}
                          </Typography>
                        </Box>

                        {/* Filter By */}
                        <Box>
                          <Typography
                            sx={{
                              color: "#414958",
                              fontFamily: "Roboto",
                              fontWeight: 600,
                              fontSize: { xs: "12px", sm: "14px", md: "16px" },
                            }}
                          >
                            Filtered by
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: { xs: "10px", sm: "12px", md: "14px" },
                              color: "#183059",
                            }}
                          >
                            {/* {reportData && reportData.reportType
                          ? Array.isArray(reportData.reportType)
                            ? reportData.reportType
                              .map(
                                (type) =>
                                  type.charAt(0).toUpperCase() + type.slice(1)
                              )
                              .join(", ")
                            : reportData.reportType.charAt(0).toUpperCase() +
                            reportData.reportType.slice(1)
                          : "None"} */}
                            {getFilteredByValue()}
                          </Typography>
                        </Box>

                        {/* Speed Limit - Only for Overspeed */}
                        {reportType === "overspeed" && (
                          <Box>
                            <Typography
                              sx={{
                                color: "#414958",
                                fontFamily: "Roboto",
                                fontWeight: 600,
                                fontSize: {
                                  xs: "12px",
                                  sm: "14px",
                                  md: "16px",
                                },
                              }}
                            >
                              Speed Limit
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: {
                                  xs: "10px",
                                  sm: "12px",
                                  md: "14px",
                                },
                                color: "#183059",
                              }}
                            >
                              {settings?.maxSpeed} km/h
                            </Typography>
                          </Box>
                        )}

                        {reportType === "halt" && (
                          <Box>
                            <Typography
                              sx={{
                                color: "#414958",
                                fontFamily: "Roboto",
                                fontWeight: 600,
                                fontSize: {
                                  xs: "12px",
                                  sm: "14px",
                                  md: "16px",
                                },
                              }}
                            >
                              Inactive Time
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: {
                                  xs: "10px",
                                  sm: "12px",
                                  md: "14px",
                                },
                                color: "#183059",
                              }}
                            >
                              {
                                transformedReportData?.summary
                                  ?.totalInactiveTime?.string
                              }
                            </Typography>
                          </Box>
                        )}
                        {reportType === "halt" && (
                          <Box>
                            <Typography
                              sx={{
                                color: "#414958",
                                fontFamily: "Roboto",
                                fontWeight: 600,
                                fontSize: {
                                  xs: "12px",
                                  sm: "14px",
                                  md: "16px",
                                },
                              }}
                            >
                              Dead Time
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: {
                                  xs: "10px",
                                  sm: "12px",
                                  md: "14px",
                                },
                                color: "#183059",
                              }}
                            >
                              {
                                transformedReportData?.summary?.totalDeadTime
                                  ?.string
                              }
                            </Typography>
                          </Box>
                        )}

                        {/* Total Records */}
                        <Box>
                          <Typography
                            sx={{
                              color: "#414958",
                              fontFamily: "Roboto",
                              fontWeight: 600,
                              fontSize: { xs: "12px", sm: "14px", md: "16px" },
                            }}
                          >
                            Records
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto",
                              fontWeight: 400,
                              fontSize: { xs: "10px", sm: "12px", md: "14px" },
                              color: "#183059",
                            }}
                          >
                            {
                              // Object.entries(selectedCategories)
                              //   .filter(
                              //     ([_, data]) =>
                              //       data.selectedItems.length > 0 ||
                              //       data.isAllSelected
                              //   )
                              //   .map(([key, data]) =>
                              //     data.isAllSelected
                              //       ? `All ${
                              //           key.charAt(0).toUpperCase() + key.slice(1)
                              //         }`
                              //       : data.selectedItems.length <= 2
                              //       ? data.selectedItems.join(", ")
                              //       : `${data.selectedItems.length} ${
                              //           key.charAt(0).toUpperCase() + key.slice(1)
                              //         }`
                              //   )
                              //   .join(", ") ||
                              reportData && reportData.data
                                ? `${reportData.data.length}`
                                : "0"
                            }
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#F2F2F3",
                        borderRadius: "0px 0px 8px 8px",
                        minHeight: { xs: "150px", sm: "175px", md: "200px" },
                        overflow: "auto",
                        "&::-webkit-scrollbar": {
                          width: { xs: "6px", sm: "8px", md: "10px" },
                          height: { xs: "6px", sm: "8px", md: "10px" },
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#647490",
                          borderRadius: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <ReportTable reportData={transformedReportData} />
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#647490",
                        p: { xs: 0.25, sm: 0.5, md: 1 },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: { xs: "16px", sm: "24px", md: "32px" },
                        height: { xs: "auto", sm: "50px", md: "80px" },
                        borderRadius: "12px",
                        gap: { xs: 2, sm: 0 },
                      }}
                    >
                      <ExportOptions
                        reportData={exportReportData}
                        exportFormat={exportFormat}
                        setExportFormat={setExportFormat}
                        reportType={reportType}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          gap: { xs: 0.5, sm: 0.75, md: 1 },
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        <Button
                          variant="contained"
                          onClick={handleEmailClick}
                          sx={{
                            width: { xs: "50%", sm: "90px", md: "90px" },
                            height: { xs: "36px", sm: "42px", md: "40px" },
                            gap: { xs: "4px", sm: "6px", md: "8px" },
                            borderRadius: "8px",
                            borderWidth: "1px",
                            borderStyle: "solid",
                            borderColor: "#183059",
                            paddingTop: { xs: "8px", sm: "10px", md: "12px" },
                            paddingRight: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                            },
                            paddingBottom: {
                              xs: "8px",
                              sm: "10px",
                              md: "12px",
                            },
                            paddingLeft: { xs: "12px", sm: "14px", md: "16px" },
                            backgroundColor: "#20A8E0",
                            color: "white",
                            fontSize: { xs: "8px", sm: "9px", md: "14px" },
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#00acc1" },
                          }}
                        >
                          <img
                            src={Email}
                            alt="mail"
                            style={{
                              marginRight: "2px",
                              width: window.innerWidth < 600 ? "12px" : "20px",
                              height: window.innerWidth < 600 ? "12px" : "20px",
                            }}
                          />
                          {t("Mail")}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handlePrint}
                          sx={{
                            width: { xs: "50%", sm: "90px", md: "90px" },
                            height: { xs: "36px", sm: "42px", md: "40px" },
                            gap: { xs: "4px", sm: "6px", md: "8px" },
                            borderRadius: "8px",
                            borderWidth: "1px",
                            borderStyle: "solid",
                            borderColor: "#183059",
                            paddingTop: { xs: "6px", sm: "8px", md: "10px" },
                            paddingRight: {
                              xs: "10px",
                              sm: "12px",
                              md: "14px",
                            },
                            paddingBottom: { xs: "6px", sm: "8px", md: "10px" },
                            paddingLeft: { xs: "10px", sm: "12px", md: "14px" },
                            backgroundColor: "#20A8E0",
                            color: "white",
                            fontSize: { xs: "8px", sm: "9px", md: "14px" },
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#00acc1" },
                          }}
                        >
                          <img
                            src={Print}
                            alt="print"
                            style={{
                              marginRight: "4px",
                              width: window.innerWidth < 600 ? "12px" : "20px",
                              height: window.innerWidth < 600 ? "12px" : "20px",
                            }}
                          />
                          {t("Print")}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* </Modal> */}
            <EmailReportModal
              open={emailModalOpen}
              onClose={() => setEmailModalOpen(false)}
              organizationUsers={users}
              reportType={getReportName(reportType)}
              exportFormat={exportFormat}
              reportCategory={reportType}
              dateOptions={dateOptions}
              selectedCategories={selectedCategories}
              selectedSiteId={selectedSiteId}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default ReportGenerator;
